import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type {
  ActionBody,
  CardView,
  GameView,
  PlayerId,
  TargetRef,
  TargetRequest,
  WireEvent,
} from "../api/types";
import type { CatalogCard } from "../api/client";
import * as api from "../api/client";
import {
  GameChannelError,
  joinGameChannel,
  type GameChannelHandle,
  type GameStateResponse,
} from "../api/rooms";
import { useCatalogAndCollection } from "../api/useCatalog";
import { useDebouncedLogPersist } from "../logic/useDebouncedLogPersist";
import { catalogToCardView } from "../logic/catalogToCardView";
import { pixelTheme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { YOU } from "../theme/players";
import {
  clearAttackerSelection,
  emptySelection,
  parseTargetKey,
  pendingKey,
  targetKey,
  type Selection,
} from "../logic/selection";
import { canBlockAny, isBlockPairLegal } from "../logic/canBlock";
import {
  MIN_STEP_DELAY_MS,
  needsPacingFloor,
  splitEventsIntoFrames,
} from "../logic/animateSteps";
import {
  initialBattleState,
  reduceBattle,
  type BattleState,
} from "../logic/battleReducer";
import { selectGameView } from "../logic/selectGameView";
import { forcedAttackerIds, hasReckless } from "../logic/forcedAttackers";
import { feedFromEvents } from "../logic/feedFromEvents";
import { useClickOutsideCards } from "../logic/useClickOutsideCards";
import {
  actionButtonSettled,
  endTurnAction,
  primaryAction,
  type ActionContext,
} from "../logic/actionFor";
import { ChoiceModal } from "../components/ChoiceModal";
import { HandRow } from "../components/HandRow";
import { PreviewPanel } from "../components/PreviewPanel";
import { EventLog } from "../components/EventLog";
import { GameOverBanner } from "../components/GameOverBanner";
import { VolumePanel } from "../components/VolumePanel";
import { ZoneViewer, type ZoneViewerTargeting } from "../components/ZoneViewer";
import { PlayerPanel } from "../components/PlayerPanel";
import { Field, type FieldInteraction } from "../components/Field";
import { BlockLines } from "../components/BlockLines";
import { TargetOverlay } from "../components/TargetOverlay";
import { OPPONENT } from "../theme/players";
import { phaseLabel } from "../logic/actionFor";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { shouldAcknowledgeRevealClose, type OpenZone } from "../logic/revealClose";
import { PlayerRail } from "../components/PlayerRail";
import { start as startBattleAudio } from "../logic/battleAudio";
import { playSfx } from "../logic/sfx";
import { deriveBattleSfx } from "../logic/battleSfx";
import { ownCardClickIntent, opponentCardClickIntent } from "../logic/combatClick";
import { useBattleAnimations } from "../logic/useBattleAnimations";
import { BoardRefsProvider, cardKey } from "../logic/boardRefs";
import { CardOverridesProvider } from "../logic/cardOverrides";
import { effectiveDurationMs } from "../logic/animSpeed";
import { DURATIONS } from "../logic/motion";
import { ghostBtn, panelStyle, primaryBtn } from "../theme/styles";
import { useAnnouncer } from "../logic/LiveAnnouncer";
import { hasVisibleDiff } from "../logic/battleDiff";
import {
  BounceAnchorGhost,
  PlayCloneCard,
} from "../components/battle/PlayCloneCard";
import { CombatLungeOverlay } from "../components/battle/CombatLungeOverlay";
import {
  ErrorScreen,
  LoadingScreen,
} from "../components/battle/BattleScreens";
import { EndTurnButton } from "../components/battle/EndTurnButton";
import {
  SpectatorHeader,
  spectatorHeadline,
} from "../components/battle/SpectatorHeader";
import { BlockHint } from "../components/battle/BlockHint";

export interface Targeting {
  request: TargetRequest;
  candidateEntities: Set<number>;
  candidatePlayers: Set<PlayerId>;
  pickedEntities: Map<number, number>;
  pickedPlayers: Map<PlayerId, number>;
  onToggle: (ref: TargetRef) => void;
  onConfirm: () => void;
  onClear: () => void;
}

const DEFAULT_VIEWER: PlayerId = 0;

// Stable empties so the displayed-attackers / displayed-blocks props
// keep referential equality across renders and Field doesn't re-derive.
const EMPTY_NUMBER_SET: Set<number> = new Set<number>();
const EMPTY_BLOCK_MAP: Map<number, Set<number>> = new Map();

// "This engine session no longer exists", across both transports:
// the HTTP route responds 404, the game channel replies
// `reason: "not_found"`.
function isNotFoundError(e: unknown): boolean {
  if (e instanceof api.ApiError) return e.status === 404;
  if (e instanceof GameChannelError) return e.reason === "not_found";
  return false;
}

export function BattlePage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const location = useLocation();
  // When BattlePage is reached via NpcView's challenge-fight modal, the
  // navigator stamps `state: { returnTo }` so the post-game button can
  // bounce the player back to the NPC's world page instead of /lobby.
  // Lost on hard refresh (location.state is in-memory) — falls back to
  // the lobby/room destination, which is fine for a deep link.
  const challengeReturnTo =
    typeof (location.state as { returnTo?: unknown } | null)?.returnTo === "string"
      ? ((location.state as { returnTo: string }).returnTo)
      : null;
  const { gameId } = useParams<{ gameId: string }>();
  // Spectator mode: `?spectate=1` on the URL. Set by RoomPage when the
  // viewer is not a seated player. Toggles read-only UI and unlocks
  // the perspective-switch control.
  const spectating = new URLSearchParams(location.search).get("spectate") === "1";

  // Redirect when the URL's gameId no longer matches the user's
  // active match. Scenario: a tab sat on `/battle/<old-id>` after that
  // match ended, the user then started a new game elsewhere (NpcView
  // challenge button, room, …), and now this tab is pointed at a dead
  // session id. State polls already migrate to the right id via the
  // channel resumer, so the SPA appears to render the correct game —
  // but `submitAction` POSTs to the URL's stale id and gets a 404,
  // which the user reads as "I can't play cards." `useActiveGameRedirect`
  // does this same redirect on every non-battle page; the battle page
  // was the one exception, which is exactly how the stale URL
  // survived. Spectators don't have an active-match row, so we leave
  // their URL alone.
  useEffect(() => {
    if (spectating || !gameId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { active } = await api.getActiveGame();
        if (cancelled || !active) return;
        if (String(active.id) !== String(gameId)) {
          navigate(`/battle/${active.id}`, { replace: true });
        }
      } catch {
        // 401 / transient — the page's own state-fetch will surface
        // the right error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId, navigate, spectating]);
  // The seat the spectator is currently watching from. Always 0 for
  // players (their own seat is what `snapshot.viewer` returns).
  const [spectatorPerspective, setSpectatorPerspective] = useState<PlayerId>(0);
  const [state, dispatch] = useReducer(reduceBattle, initialBattleState);

  // Persist the rolling event log to localStorage so a page refresh
  // mid-match doesn't blank the entire feed — the engine session
  // re-fetches its current state, but the per-event banners are SPA
  // derivations the server has no way to replay.
  //
  // Hydrate on mount (gameId change) BEFORE the snapshot load fires
  // its banner seeding (the "loaded" reducer case only seeds when
  // `eventLog.length === 0`, so a successful rehydrate suppresses the
  // duplicate). Persist on every event-log update.
  const logStorageKey = gameId ? `doi/battle-log/${gameId}` : null;
  useEffect(() => {
    if (!logStorageKey) return;
    try {
      const raw = window.localStorage.getItem(logStorageKey);
      if (raw == null) return;
      const entries = JSON.parse(raw);
      if (Array.isArray(entries) && entries.length > 0) {
        dispatch({ type: "hydrate_event_log", entries });
      }
    } catch {
      // Malformed cache — ignore and let the snapshot seed fresh.
    }
    // Only re-run when the game id changes (mount + navigation between
    // matches in the same tab).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logStorageKey]);
  // Persisting re-stringifies the whole growing log, so appends are
  // debounced (~500 ms, flushed on unmount) instead of written per
  // event. A terminal match clears the key — gameIds are never reused.
  useDebouncedLogPersist(
    logStorageKey,
    state.eventLog,
    Boolean(state.terminal),
  );
  // The reducer holds v2 wire state (entities Map + defs Map + scalars +
  // ...); the rest of this page (HandRow, Field, EventLog, PreviewPanel,
  // …) still consumes the v1-shape `GameView` augmented with
  // server-attached wrapper fields (persona / seat / opponent_connected /
  // …). `selectGameView` is the pure projection; the wrappers come
  // from `/api/games/:id/state2` and are held in a separate piece of
  // local state because they don't change on apply_events.
  const baseSnapshot = useMemo(() => selectGameView(state), [state]);
  const [wrapper, setWrapper] = useState<{
    seat?: 0 | 1;
    room_id?: string | null;
    ai_persona?: api.AiPersona;
    human_persona?: api.HumanPersona;
    spectating?: boolean;
    seat_personas?: Record<"0" | "1", api.SeatPersona>;
    opponent_connected?: boolean;
    idle_seconds?: number;
  }>({});
  const snapshot = useMemo(() => {
    if (!baseSnapshot) return null;
    return Object.assign({}, baseSnapshot, wrapper);
  }, [baseSnapshot, wrapper]);
  const { eventLog } = state;
  // Mirror reducer state for synchronous handlers that need fresh state
  // without waiting on a re-render closure.
  const stateRef = useRef<BattleState>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const [selection, setSelection] = useState<Selection>(emptySelection());
  const [animating, setAnimating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optimistic mana deduction for the current in-flight play. Set the
  // moment `submitAction` commits a `play` body (before the hover beat
  // and the network round trip), cleared when the engine's `mana`
  // scalar event lands and `snapshot.players[viewer].mana` drops to
  // match. The HandRow subtracts this from the snapshot mana so other
  // hand cards grey out the instant the user commits — without the
  // 300-500 ms gap between drag-release and event apply, and without
  // the "grey → briefly ungrey → grey again" flicker the previous
  // `castingEid`-based deduction had (the lookup broke the moment
  // the entity moved out of hand mid-batch).
  const [pendingPlay, setPendingPlay] = useState<
    | {
        cost: number;
        manaBefore: number;
      }
    | null
  >(null);
  // Concede / opponent-ended fire `abort()` so the animation queue
  // stops instead of ticking out under the banner.
  const animationAbortRef = useRef<AbortController | null>(null);
  // The turn-qualified pendingKey of where the engine is ACTUALLY
  // waiting for input — the projected-final pending of the in-flight
  // event batch. While `animating` is true, the displayed snapshot can
  // lag many beats behind this (e.g. it still shows our own "Declare
  // Attackers" while the engine has already resolved combat and handed
  // the turn to the opponent). The contextual action button gates on
  // this so it never offers a stale decision (the "Attack (1)" button
  // lingering through the opponent's turn) that the engine would reject.
  // Read during render; safe because it is written synchronously right
  // before `setAnimating(true)`, whose re-render reads the fresh value.
  const liveTargetRef = useRef<string | null>(null);
  // The state-commit closure that `applyEventBatch` is about to run
  // (or has just run) for the current in-flight animation. The
  // animation/lunge waits happen BEFORE the commit fires so that
  // attackers about to die are still mounted for the FX wrappers, so
  // there is a real window where the animation is "in flight" but
  // `BattleState` does not yet reflect the events that triggered it.
  // `cancelAnimation` invokes whatever is stored here to fast-forward
  // the state before processing a user input that arrived inside that
  // window. Cleared the moment the commit actually runs (either by
  // the natural animation path or by us calling it from
  // `cancelAnimation`) so we never double-dispatch.
  const pendingCommitRef = useRef<(() => void) | null>(null);
  const [openZone, setOpenZone] = useState<OpenZone>(null);
  // The victory / defeat banner can be temporarily minimized so the
  // player can inspect the final board and event log. The "Continue"
  // button (rendered in the EndTurnButton slot at end-of-game) brings
  // it back. Reset whenever a fresh game starts (no result on snapshot).
  const [gameOverMinimized, setGameOverMinimized] = useState(false);
  useEffect(() => {
    if (!snapshot?.result) setGameOverMinimized(false);
  }, [snapshot?.result]);
  const [aiPersona, setAiPersona] = useState<api.AiPersona | null>(null);
  const [humanPersona, setHumanPersona] = useState<api.HumanPersona | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);
  // Live battle-channel handle for `game:<engineSessionId>`. While
  // set, action submits and snapshot fetches ride the channel (the
  // in-battle hot path) instead of the HTTP routes; null until the
  // join completes or after it fails, in which case the HTTP
  // fallbacks in `submitAction` / `fetchStateAny` cover play.
  const gameConnRef = useRef<GameChannelHandle | null>(null);
  const [dismissedRevealKey, setDismissedRevealKey] = useState<string | null>(null);
  // For seated players, `snapshot.viewer` is the engine seat their
  // snapshot was built for. Spectators pick the seat themselves via
  // the perspective toggle; the snapshot fetch re-runs whenever
  // `spectatorPerspective` flips.
  const viewer: PlayerId = spectating
    ? spectatorPerspective
    : snapshot?.viewer === 1
      ? 1
      : DEFAULT_VIEWER;
  // Clear the optimistic mana deduction once the engine's actual
  // mana scalar has landed. `snapshot.players[viewer].mana !==
  // manaBefore` means the cast committed and the wire dropped mana
  // (or, for a rejected cast, the next turn refilled it past
  // `manaBefore`). Either way, the snapshot is now the truth and
  // the optimism should retire.
  const currentMana = snapshot?.players[viewer].mana;
  useEffect(() => {
    if (pendingPlay == null) return;
    if (currentMana == null) return;
    if (currentMana !== pendingPlay.manaBefore) {
      setPendingPlay(null);
    }
  }, [pendingPlay, currentMana]);
  const { catalogById } = useCatalogAndCollection();
  const layout = useResponsiveLayout();
  const [opponentCardCache, setOpponentCardCache] = useState<
    Map<string, CatalogCard>
  >(new Map());
  // Persistent "−N" overlay shown on each player panel for the life
  // they've taken this turn — mirrors the engine's end-of-turn cleanup
  // of creatures' marked damage.
  const [lifeLossThisTurn, setLifeLossThisTurn] = useState<[number, number]>([
    0, 0,
  ]);
  const lastSeenTurnRef = useRef<number | null>(null);

  // Phase-change a11y announcer. Reads `snapshot` from the effect's
  // closure rather than from a destructured local so the hook can sit
  // above the "snapshot still loading" early return — hook order must
  // be stable across renders.
  const announce = useAnnouncer();
  const lastPhaseRef = useRef<string | null>(null);
  const pendingForAnnounce = snapshot?.pending ?? null;
  const turnForAnnounce = snapshot?.turn ?? null;
  useEffect(() => {
    const key = pendingForAnnounce
      ? `${pendingForAnnounce.kind}:${pendingForAnnounce.player === viewer ? "you" : "opp"}:${turnForAnnounce}`
      : null;
    if (key === lastPhaseRef.current) return;
    lastPhaseRef.current = key;
    if (!pendingForAnnounce || turnForAnnounce == null) return;
    const phase = phaseLabel(pendingForAnnounce);
    const whose = pendingForAnnounce.player === viewer ? "Your" : "Opponent's";
    announce(`${whose} ${phase}, turn ${turnForAnnounce}`);
  }, [pendingForAnnounce, turnForAnnounce, viewer, announce]);

  const boardRef = useRef<HTMLDivElement | null>(null);
  // Spans the central play area (opponent battle + phase divider +
  // player battle). Tracked separately from `boardRef` so animation
  // surfaces like the play clone can be clipped to it — cards must
  // never paint outside this region (sidebar / margins are off-limits).
  const centralAreaRef = useRef<HTMLDivElement | null>(null);
  // Reference to the invisible anchor inside the opp battle area;
  // read by `playEvents` to know where to mount the opp-play hover
  // clone. Declared up here so the hook order stays stable across
  // renders (the JSX consumer down in `opponentHandAnchor` only
  // binds the ref — it doesn't re-call useRef).
  const opponentHandAnchorRef = useRef<HTMLDivElement | null>(null);
  const [centralRect, setCentralRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    const el = centralAreaRef.current;
    if (!el) return;
    const update = () => setCentralRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);
  // Compact-tier: tracks whether the player rail is expanded into the
  // overlay panel. The overlay floats above the field area; tap outside
  // or press Escape to dismiss.
  const [railExpanded, setRailExpanded] = useState(false);
  // Captured from the end-match response on the natural-terminal
  // path (engine reports game over → frontend POSTs endGame). When
  // the player clicks RETURN, `onConcede` reads this and attaches it
  // as `location.state.resumeNodeId` on the navigate-back so the NPC
  // page lands at the author-declared on_win / on_loss node.
  const [challengeResumeNodeId, setChallengeResumeNodeId] = useState<
    string | null
  >(null);
  const cardRefs = useMemo(
    () => new Map<number, HTMLDivElement | null>(),
    [],
  );
  type DragState = {
    blockerId: number;
    startX: number;
    startY: number;
    dragging: boolean;
  };
  const dragRef = useRef<DragState | null>(null);
  const suppressNextClickRef = useRef(false);
  const liveDragRef = useRef<{
    pending: GameView["pending"] | null;
    you: GameView["players"][number] | null;
    opponent: GameView["players"][number] | null;
    incomingAttackers: Set<number>;
    pendingBlocker: number | null;
    onOwnBlockerClick: (c: CardView) => void;
    onEnemyAttackerClick: (c: CardView) => void;
    onCancelPendingBlocker: () => void;
  }>({
    pending: null,
    you: null,
    opponent: null,
    incomingAttackers: new Set(),
    pendingBlocker: null,
    onOwnBlockerClick: () => {},
    onEnemyAttackerClick: () => {},
    onCancelPendingBlocker: () => {},
  });

  const animations = useBattleAnimations({ snapshot, viewer });
  // Destructure stable fields so individual effects only re-fire when
  // their genuine inputs change. Depending on the whole `animations`
  // object would re-fire on every `oppHandName` flip during opp plays.
  const {
    setPreviewOverrides: setPreviewOverridesFn,
    runFrame: runAnimatedFrame,
    boardRefs: animBoardRefs,
    applyInstant,
    beginPlayHover,
    abortPlayHover,
    markCastingDissolved,
    awaitSpellFizzle,
    finishPlayHover,
    playCombatLunge,
    playClone,
    castingDissolvedEid,
  } = animations;

  // Channel-first snapshot fetch. Falls back to the HTTP route while
  // the game channel isn't joined (yet, or at all). Every steady-state
  // refetch (Rally/Reinforce preview, gap-detect resync, claim-victory
  // poll) goes through here.
  const fetchStateAny = useCallback(
    async (preview?: api.PreviewState): Promise<GameStateResponse> => {
      const conn = gameConnRef.current;
      if (conn) return conn.fetchState(preview);
      if (!gameId) throw new Error("no game id");
      return api.getGameStateV2(gameId, preview);
    },
    [gameId],
  );

  // Rally/Reinforce live preview. While the player is picking
  // attackers/blockers, re-fetch the engine snapshot with their
  // tentative selection echoed back as `preview_attackers` /
  // `preview_blocks`. The continuous query-layer keywords surface the
  // would-be buff in the returned snapshot's per-entity stats; we
  // diff `current_attack` / `remaining_life` per eid and overlay the
  // new values via `setPreviewOverridesFn` so the field shows the
  // buffed numbers without disturbing the rest of the snapshot.
  useEffect(() => {
    if (!gameId || !snapshot) return;
    if (spectating) {
      setPreviewOverridesFn(new Map());
      return;
    }
    const pending = snapshot.pending;
    if (!pending || pending.player !== viewer) {
      setPreviewOverridesFn(new Map());
      return;
    }
    if (pending.kind !== "attackers" && pending.kind !== "blocks") {
      setPreviewOverridesFn(new Map());
      return;
    }
    const attackers: number[] =
      pending.kind === "attackers" ? Array.from(selection.attackers) : [];
    const blocks: Array<[number, number]> = [];
    if (pending.kind === "blocks") {
      for (const [attacker, blockerSet] of selection.blocks) {
        for (const blocker of blockerSet) blocks.push([blocker, attacker]);
      }
    }
    if (attackers.length === 0 && blocks.length === 0) {
      setPreviewOverridesFn(new Map());
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const snap = await fetchStateAny({
            preview_attackers: attackers,
            preview_blocks: blocks,
          });
          if (cancelled) return;
          // Walk the preview's `entities` map; for each eid whose
          // bussed stats differ from the committed snapshot's row,
          // emit a patch. Override Map keys by eid so Card renderers
          // pick the buffed numbers up at paint time.
          const patch = new Map<number, Partial<CardView>>();
          for (const [eidStr, rec] of Object.entries(snap.entities)) {
            const eid = Number(eidStr);
            let base: CardView | undefined;
            for (const seat of snapshot.players) {
              const found = seat.battle.find((c) => c.entity_id === eid);
              if (found) {
                base = found;
                break;
              }
            }
            if (!base) continue;
            const cur_atk = rec.current_attack ?? base.current_attack;
            const cur_lif = rec.current_life ?? base.current_life;
            const rem_lif = rec.remaining_life ?? base.remaining_life;
            if (
              cur_atk !== base.current_attack ||
              cur_lif !== base.current_life ||
              rem_lif !== base.remaining_life
            ) {
              patch.set(eid, {
                current_attack: cur_atk,
                current_life: cur_lif,
                remaining_life: rem_lif,
              });
            }
          }
          setPreviewOverridesFn(patch);
        } catch {
          // Network blip or stale request — leave the prior preview
          // in place rather than reverting to base stats mid-pick.
        }
      })();
    }, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    gameId,
    snapshot,
    selection.attackers,
    selection.blocks,
    viewer,
    spectating,
    setPreviewOverridesFn,
    fetchStateAny,
  ]);

  useEffect(() => {
    const stop = startBattleAudio();
    return () => stop();
  }, []);

  const clearInspect = useCallback(() => {
    setSelection((s) =>
      s.inspected == null && s.handIdx == null
        ? s
        : { ...s, inspected: null, handIdx: null },
    );
  }, []);
  useClickOutsideCards(
    selection.inspected != null || selection.handIdx != null,
    clearInspect,
  );

  const lastSettledPendingKey = useRef<string>("none");

  // Funnel for wire-event batches from both HTTP action responses and
  // channel pushes. `instant=true` skips the post-dispatch motion
  // beat (used to fast-forward to a pending decision); SFX and
  // life-loss accumulation still run. Replaces the v1
  // `applyEngineStep(step)` — events come in as a contiguous batch
  // with monotonic seqs.
  const applyEventBatch = useCallback(
    async (events: WireEvent[], instant = false): Promise<void> => {
      if (events.length === 0) return Promise.resolve();
      const cur = stateRef.current;
      if (cur.terminal) return Promise.resolve();
      const lastSeq = events[events.length - 1].seq;
      if (lastSeq <= cur.lastEventSeq) return Promise.resolve();

      // Project pre/post snapshots so the existing SFX/animation/
      // life-loss logic — all of which thinks in terms of v1 GameView
      // diffs — keeps working unchanged.
      const prevSnap = selectGameView(cur);
      const nextState = reduceBattle(cur, { type: "apply_events", events });
      const nextSnap = selectGameView(nextState);
      if (!nextSnap) return Promise.resolve();
      const feedEntries = feedFromEvents(events, { prev: cur, viewer });

      // Accumulate this batch's life loss per seat; reset when the
      // turn rolls over — same lifecycle as the engine's marked damage.
      const prevTurn = lastSeenTurnRef.current;
      const turnChanged = prevTurn != null && nextSnap.turn !== prevTurn;
      lastSeenTurnRef.current = nextSnap.turn;
      if (turnChanged) {
        setLifeLossThisTurn([0, 0]);
      } else if (prevSnap) {
        const deltas: [number, number] = [
          Math.max(0, prevSnap.players[0].life - nextSnap.players[0].life),
          Math.max(0, prevSnap.players[1].life - nextSnap.players[1].life),
        ];
        if (deltas[0] > 0 || deltas[1] > 0) {
          setLifeLossThisTurn((prev) => [
            prev[0] + deltas[0],
            prev[1] + deltas[1],
          ]);
        }
      }

      const sfxEvents = deriveBattleSfx(prevSnap, nextSnap, feedEntries, viewer);
      for (const ev of sfxEvents) {
        if (ev.delayMs && ev.delayMs > 0) {
          const sound = ev.sound;
          // Anim-speed scales the delay so SFX stays in sync with the
          // also-scaled lunge / view transitions.
          window.setTimeout(() => playSfx(sound), effectiveDurationMs(ev.delayMs));
        } else {
          playSfx(ev.sound);
        }
      }

      const wasTerminal = prevSnap?.result != null;

      // Combat lunge attribution. Pre-v2 we sourced this from feed
      // entries against `prev.combat.attackers`; v2 carries the source
      // eid directly on each `damage` event, so we just filter by
      // declared attackers (combat damage is simultaneous and blockers
      // also emit damage events — don't lunge them).
      const declaredAttackers = new Set<number>(prevSnap?.combat?.attackers ?? []);
      for (const ev of events) {
        if (ev.k === "attackers_declared") {
          for (const a of ev.attackers) declaredAttackers.add(a);
        }
      }
      const lungeAttackerEids: number[] = [];
      for (const ev of events) {
        if (ev.k !== "damage") continue;
        const src = ev.source;
        if (src == null) continue;
        if (!declaredAttackers.has(src)) continue;
        if (!lungeAttackerEids.includes(src)) lungeAttackerEids.push(src);
      }
      const enteringCombatDamage = lungeAttackerEids.length > 0;

      // Commit closure: append feed banners first (so EventLog shows
      // them in order), then apply the events to BattleState. Wrapped
      // with `pendingCommitRef` plumbing so `cancelAnimation` (called
      // when a user input arrives mid-animation) can fast-forward the
      // state by running this synchronously; the `committed` guard
      // makes it idempotent so the natural animation path and the
      // cancel path can't both fire `dispatch` for the same batch.
      // Did this batch leave combat? The engine clears `combat` when it
      // exits the combat phase; the moment it does, the player's local
      // attacker pick belonged to a combat that is now over and must be
      // dropped (see `clearAttackerSelection`). Detected on the
      // prev→next snapshot so it fires for the exact event that ends
      // combat, and applied inside `commit` (below) so the cleared
      // selection lands in the SAME render as `combat` going null —
      // never a frame later, which would flash the stale ring.
      const combatEnded = prevSnap?.combat != null && nextSnap?.combat == null;

      let committed = false;
      const commit = () => {
        if (committed) return;
        committed = true;
        if (pendingCommitRef.current === commit) {
          pendingCommitRef.current = null;
        }
        for (const entry of feedEntries) {
          dispatch({ type: "log_banner", entry });
        }
        dispatch({ type: "apply_events", events });
        if (combatEnded) setSelection(clearAttackerSelection);
      };
      pendingCommitRef.current = commit;

      if (instant) {
        applyInstant(commit);
        if (!wasTerminal && nextSnap.result && gameId) {
          if (spectating) {
            // Spectators don't authenticate end_match — they just
            // stop on the terminal snapshot and wait for the
            // game_ended channel push to navigate.
            dispatch({ type: "engine_finalized" });
          } else {
            void (async () => {
              try {
                const r = await api.endGame(gameId);
                if (r.challenge?.resume_node) {
                  setChallengeResumeNodeId(r.challenge.resume_node);
                }
                dispatch({ type: "engine_finalized" });
              } catch {
                // Already-deleted session — banner is still useful.
              }
            })();
          }
        }
        return Promise.resolve();
      }

      // Lunge BEFORE dispatch so attackers that die in this batch are
      // still mounted (their FX-wrapper refs are findable). Await the
      // lunge's returned Promise — it resolves once every motion clone's
      // onAnimationComplete (or safety timeout) has run cleanup, which
      // is what un-hides the original's `el.style.visibility`.
      if (enteringCombatDamage) {
        const refSnap = prevSnap ?? nextSnap;
        await playCombatLunge({
          snapshot: refSnap,
          viewer,
          attackerEids: lungeAttackerEids,
        });
        // Drop the attacker→blocker lines now that the lunge has
        // landed, but BEFORE runAnimatedFrame FLIPs the dying
        // creatures into the graveyard. Otherwise the lines stay
        // pinned to the cards and trail them all the way to the
        // grave, which looks broken. (No-op for the attacking seat,
        // whose `blocks` set is empty.)
        setSelection((s) =>
          s.blocks.size === 0
            ? s
            : { ...s, blocks: new Map(), pendingBlocker: null },
        );
      }
      const stepPromise = runAnimatedFrame(
        prevSnap,
        nextSnap,
        feedEntries,
        commit,
        viewer,
      );

      if (!wasTerminal && nextSnap.result && gameId) {
        if (spectating) {
          dispatch({ type: "engine_finalized" });
        } else {
          void (async () => {
            try {
              const r = await api.endGame(gameId);
              // On challenge matches the server returns the
              // author-declared resume node — capture it here so the
              // subsequent RETURN-button click can pass it forward as
              // location.state on the navigate-back.
              if (r.challenge?.resume_node) {
                setChallengeResumeNodeId(r.challenge.resume_node);
              }
              dispatch({ type: "engine_finalized" });
            } catch {
              // Already-deleted session — banner is still useful.
            }
          })();
        }
      }

      return stepPromise;
    },
    [runAnimatedFrame, applyInstant, gameId, viewer, spectating, playCombatLunge],
  );

  const resetSelectionIfPendingChanged = useCallback(
    (snap: GameView) => {
      const key = pendingKey(snap.pending, snap.turn);
      if (key !== lastSettledPendingKey.current) {
        lastSettledPendingKey.current = key;
        // Reckless creatures are forced into the attacker list by the
        // engine regardless of what the player submits — pre-seed them
        // here so the attack-phase UI shows them already-selected.
        const forced = forcedAttackerIds(snap, viewer);
        setSelection((s) => ({
          ...s,
          handIdx: null,
          attackers: forced,
          blocks: new Map(),
          pendingBlocker: null,
          targetPicks: new Set(),
        }));
      }
    },
    [viewer],
  );

  // Snapshot can change mid-attackers-phase (a Reckless trap springs,
  // a Rush creature enters via a triggered ability, …). Re-seed the
  // forced set whenever it grows so newcomers are auto-selected.
  useEffect(() => {
    if (!snapshot) return;
    if (snapshot.pending?.kind !== "attackers") return;
    if (snapshot.pending.player !== viewer) return;
    const forced = forcedAttackerIds(snapshot, viewer);
    if (forced.size === 0) return;
    setSelection((s) => {
      let changed = false;
      const next = new Set(s.attackers);
      for (const id of forced) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? { ...s, attackers: next } : s;
    });
  }, [snapshot, viewer]);

  const playEvents = useCallback(
    async (events: WireEvent[]) => {
      if (events.length === 0) return;
      animationAbortRef.current?.abort();
      const ac = new AbortController();
      animationAbortRef.current = ac;
      // Record where this batch lands — the decision point the engine is
      // waiting on once every event has been applied. The displayed
      // snapshot walks toward this over the paced replay; the action
      // button stays inert until the two agree (see `animationSettled`).
      const projectedFinal = selectGameView(
        reduceBattle(stateRef.current, { type: "apply_events", events }),
      );
      liveTargetRef.current = projectedFinal
        ? pendingKey(projectedFinal.pending, projectedFinal.turn)
        : null;
      setAnimating(true);
      // Track how many events have actually been committed so a
      // mid-batch abort can flush the rest in one synchronous dispatch.
      // Without that, the NEW playEvents that fired the abort starts
      // applying NEW events whose seqs leapfrog the OLD-batch tail,
      // and the reducer's seq-skip silently drops the OLD tail —
      // BattleState then disagrees with the engine on which cards
      // are where.
      let consumed = 0;
      const onAbort = () => {
        const tail = events.slice(consumed);
        if (tail.length > 0) dispatch({ type: "apply_events", events: tail });
      };
      ac.signal.addEventListener("abort", onAbort, { once: true });
      try {
        // The v2 wire returns the whole sequence — declare attackers,
        // blockers, damage, end turn, the opponent's full turn, our
        // next main — in one events array. Replaying it as one frame
        // blurs every beat into one ~380 ms crossfade and fires the
        // combat lunge while half the attackers haven't been declared
        // yet. Chunk at Phase / Turn boundaries so each beat gets its
        // own transition, lunge derivation, and the inter-step floor
        // that v1 used to pace.
        const frames = splitEventsIntoFrames(events);
        const floor = effectiveDurationMs(MIN_STEP_DELAY_MS);
        for (let i = 0; i < frames.length; i++) {
          if (ac.signal.aborted) return;
          const frame = frames[i];
          const startSnap = selectGameView(stateRef.current);
          const projected = selectGameView(
            reduceBattle(stateRef.current, {
              type: "apply_events",
              events: frame,
            }),
          );
          const visible =
            !startSnap || !projected || hasVisibleDiff(startSnap, projected);
          // Opp-play hover. The user's own plays mount a hover clone
          // in `submitAction` before the network round-trip; the opp
          // path has no equivalent — opp casts (especially spells)
          // never get rendered between the hand-out and the
          // graveyard-in. Detect the hand→stack move for an
          // opp-controlled eid and mount the same PlayClone overlay
          // at the opp-hand anchor's rect, so the user reads the
          // card for one hover beat before it lands. The entity /
          // def lookup goes against the PROJECTED state (the frame
          // includes the `Reveal` that ships the EntityRecord +
          // EntityDef the first time the viewer sees an opp card —
          // before applying it, `stateRef.current` has no record
          // for the eid at all, so the previous version of this
          // detector silently fell out of the `rec && def` gate and
          // the hover never mounted).
          const projectedState = reduceBattle(stateRef.current, {
            type: "apply_events",
            events: frame,
          });
          const oppPlayMove = frame.find(
            (ev): ev is WireEvent & { k: "move" } =>
              ev.k === "move" &&
              ev.from === "hand" &&
              ev.to === "stack" &&
              projectedState.entities.get(ev.eid)?.controller !== viewer,
          );
          if (oppPlayMove) {
            const rec = projectedState.entities.get(oppPlayMove.eid);
            const def = rec?.def_id
              ? projectedState.defs.get(rec.def_id)
              : null;
            const anchor = opponentHandAnchorRef.current;
            const anchorRect = anchor?.getBoundingClientRect();
            if (rec && def && anchorRect) {
              const w = 180;
              const h = (w * 7) / 5;
              const rect = new DOMRect(
                anchorRect.left + anchorRect.width / 2 - w / 2,
                anchorRect.top,
                w,
                h,
              );
              const cardForClone: CardView = {
                entity_id: rec.eid,
                def_id: def.def_id,
                name: def.name,
                type: def.type,
                cost: def.cost,
                base_attack: def.base_attack,
                base_life: def.base_life,
                current_attack: def.base_attack,
                current_life: def.base_life,
                damage_marked: 0,
                remaining_life: def.base_life,
                can_attack: false,
                is_attacking: false,
                has_script: def.has_script,
                flavor: def.flavor ?? null,
                rules_text: def.rules_text ?? null,
                image_url:
                  def.image_url ?? `/card-images/${def.def_id}`,
                faction: def.faction ?? "humans",
                race: def.race ?? null,
                class: def.class ?? null,
                expansion_set: def.expansion_set ?? null,
                rarity: def.rarity ?? null,
                summon_only: def.summon_only,
                seal_counters: 0,
                face_down: false,
                keywords: [],
              };
              await beginPlayHover({
                eid: oppPlayMove.eid,
                card: cardForClone,
                rect,
                // The viewer didn't choose this card — dwell a clear
                // half-second so it's readable before the cast
                // animation (spell fizzle / zone move) carries it off.
                hoverMs: DURATIONS.oppPlayHover,
              });
              // beginPlayHover returns at full-hover for non-spells
              // and at the mid-hover dissolve mark for spells; in
              // both cases the subsequent frames carry the move into
              // its final zone. `finishPlayHover` unmounts the
              // overlay for spells (non-spells unmount on the next
              // commit via `clearCloneOnDispatch`).
              if (cardForClone.type === "spell") {
                await awaitSpellFizzle();
                finishPlayHover();
              }
            }
          }
          const startedAt = performance.now();
          // Race the chunk's apply against abort. The motion-frame
          // promise resolves after a fixed beat (~380 ms); without
          // the race a user-input mid-animation would wait the tail
          // of that beat before cancellation could land, which reads
          // as the click being ignored.
          const applyP = applyEventBatch(frame, !visible);
          const abortP = new Promise<void>((r) => {
            if (ac.signal.aborted) return r();
            ac.signal.addEventListener("abort", () => r(), { once: true });
          });
          await Promise.race([applyP, abortP]);
          consumed += frame.length;
          if (ac.signal.aborted) return;
          const isLast = i === frames.length - 1;
          // Phase-only chunks have no visible board diff (the apply
          // above ran instantly) but still hold the floor — they're
          // the inter-phase beat the splitter cuts them out for.
          if (isLast || !needsPacingFloor(frame, visible)) continue;
          // Pacing: ensure each chunk takes at least `floor` ms total,
          // but do NOT add `floor` on top of an already-spent motion
          // beat. The frame's layout transition already takes ~380 ms;
          // adding `floor` on top of that would make every chunk
          // 680 ms.
          const elapsed = performance.now() - startedAt;
          const remaining = floor - elapsed;
          if (remaining <= 0) continue;
          await new Promise<void>((r) => {
            if (ac.signal.aborted) return r();
            const timer = window.setTimeout(r, remaining);
            ac.signal.addEventListener(
              "abort",
              () => {
                window.clearTimeout(timer);
                r();
              },
              { once: true },
            );
          });
        }
      } finally {
        // Gate the cleanup on "I am still the active animation". A
        // concurrent `playEvents` (e.g. the user's just-fired action
        // after `cancelAnimation` aborted us) will have already
        // re-assigned `animationAbortRef` and called `setAnimating(true)`;
        // we must NOT clobber that with our own `setAnimating(false)`.
        if (animationAbortRef.current === ac) {
          setAnimating(false);
          animationAbortRef.current = null;
        }
        if (!ac.signal.aborted) {
          const final = selectGameView(stateRef.current);
          if (final) resetSelectionIfPendingChanged(final);
        }
      }
    },
    [
      applyEventBatch,
      resetSelectionIfPendingChanged,
      viewer,
      beginPlayHover,
      awaitSpellFizzle,
      finishPlayHover,
    ],
  );

  // Animation-cancel-on-user-input. When the user clicks PLAY, taps an
  // attacker, or otherwise wants to act, run this BEFORE checking
  // `animating` so the input never gets silently dropped by an
  // in-flight animation. Fast-forwards the BattleState (by running
  // the pending commit closure synchronously) and aborts the running
  // `playEvents` so its pacing-floor await resolves immediately. Idle
  // when nothing is animating.
  const cancelAnimation = useCallback(() => {
    const pending = pendingCommitRef.current;
    if (pending) {
      pendingCommitRef.current = null;
      pending();
    }
    animationAbortRef.current?.abort();
    setAnimating(false);
  }, []);

  // Shared "initial snapshot landed" path for both transports — the
  // channel `state` request issued right after the game-channel join,
  // and the HTTP fallback in `loadViaHttp`.
  const applyLoadedSnapshot = useCallback(
    (snap: GameStateResponse) => {
      dispatch({ type: "loaded", snapshot: snap });
      setWrapper({
        seat: snap.seat,
        room_id: snap.room_id,
        ai_persona: snap.ai_persona,
        human_persona: snap.human_persona,
        spectating: snap.spectating,
        seat_personas: snap.seat_personas,
        opponent_connected: snap.opponent_connected,
        idle_seconds: snap.idle_seconds,
      });
      if (snap.ai_persona) setAiPersona(snap.ai_persona);
      if (snap.human_persona) setHumanPersona(snap.human_persona);
      if (snap.room_id) setRoomId(snap.room_id);
      const projected = selectGameView(
        reduceBattle(initialBattleState, { type: "loaded", snapshot: snap }),
      );
      if (projected) resetSelectionIfPendingChanged(projected);
    },
    [resetSelectionIfPendingChanged],
  );

  const handleLoadError = useCallback(
    (e: unknown) => {
      if (e instanceof api.ApiError && e.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      if (isNotFoundError(e)) {
        // The match is gone for good: its Live process has stopped, so
        // `active_for` now returns nil and the lobby's
        // `useActiveGameRedirect` won't bounce us straight back.
        // Spectators land during a teardown race and keep the room
        // context (the ErrorScreen's BACK uses roomId when known).
        if (spectating) {
          setError("This match is no longer available.");
          return;
        }
        navigate("/lobby", { replace: true });
        return;
      }
      // engine_error and friends: the match may still look "active" — a
      // live process the engine just can't serve yet (e.g. a server
      // restart still rebuilding the session). Do NOT navigate to
      // /lobby here: `useActiveGameRedirect` sees the still-active match
      // and sends us right back, an infinite redirect loop. Park on the
      // error screen instead, which renders a clear message (see
      // `friendlyError`) and a manual BACK TO LOBBY.
      setError(e instanceof Error ? e.message : String(e));
    },
    [navigate, spectating],
  );

  // HTTP cold-load. Used when the channel can't carry the initial
  // snapshot: the player has no active-match row for this URL (dead
  // session — the 404 → /lobby redirect lives in `handleLoadError`)
  // or the game-channel join itself failed.
  const loadViaHttp = useCallback(async () => {
    if (!gameId) return;
    try {
      const snap = await api.getGameStateV2(
        gameId,
        spectating ? { viewer_seat: spectatorPerspective } : undefined,
      );
      applyLoadedSnapshot(snap);
    } catch (e) {
      handleLoadError(e);
    }
  }, [
    gameId,
    spectating,
    spectatorPerspective,
    applyLoadedSnapshot,
    handleLoadError,
  ]);

  const submitAction = useCallback(
    async (body: ActionBody, opts?: { releaseRect?: DOMRect }) => {
      if (!gameId) return;
      // Animation-cancel-on-input: if an animation is still running
      // from the previous batch (e.g. the post-cast pacing tail), snap
      // its state forward instead of silently dropping this click. The
      // user is allowed to be impatient.
      cancelAnimation();
      // Spectator mode is strictly read-only. The server would 403
      // anyway (`:spectators_read_only`) — short-circuit here so the
      // SPA never spawns a request the audience can't make.
      if (spectating) return;
      // Guard rail: refuse to submit while pending belongs to
      // the other seat (covers a PvP race between opponent action
      // landing and our snapshot refreshing).
      const pending = snapshot?.pending;
      if (pending && pending.player !== viewer) return;
      setError(null);
      // Remember the eid we tried to cast so we can detect a no-target
      // cancel (card returns to hand with no Played/Cast feed entry)
      // and chirp the error sound. The engine doesn't surface this as
      // a discrete failure — it just leaves the card where it was.
      let castEid: number | null = null;
      if (body.kind === "play") {
        const me = snapshot?.players[viewer];
        castEid = me?.hand?.[body.hand_idx]?.entity_id ?? null;
        const card = me?.hand?.[body.hand_idx];
        if (castEid != null && card) {
          // Lock in the optimistic mana deduction BEFORE the await
          // so the hand cards grey out on the very next render.
          // `pendingPlay` is cleared by a `useEffect` watching the
          // snapshot mana (see below).
          setPendingPlay({ cost: card.cost, manaBefore: me?.mana ?? 0 });
          // Cast chime fires the moment the play is committed, for all
          // card types — independent of whether we land the hover clone.
          playSfx("ai_spell_cast");
          const rect =
            opts?.releaseRect ??
            animBoardRefs.get(cardKey(castEid))?.getBoundingClientRect() ??
            null;
          if (rect) {
            await beginPlayHover({ eid: castEid, card, rect });
          }
        }
      }
      // Submitted picks are consumed by the engine — clear them
      // immediately so the next picker (back-to-back yields from e.g.
      // Chancery Magus recasting graveyard spells) opens with no
      // selection. `resetSelectionIfPendingChanged` would otherwise miss
      // when two consecutive prompts produce the same pendingKey.
      if (body.kind === "target") {
        setSelection((s) => ({ ...s, targetPicks: new Set() }));
      }
      const requestId = crypto.randomUUID();
      const payload: ActionBody = { ...body, request_id: requestId };
      try {
        // In-battle hot path: submit over the game channel (no CSRF /
        // session / auth plug work per action). HTTP route remains as
        // the fallback while the channel isn't joined.
        const conn = gameConnRef.current;
        const resp = conn
          ? await conn.submitAction(payload)
          : await api.submitGameActionV2(gameId, payload);
        if ("stale" in resp && resp.stale) return;
        await playEvents(resp.viewer_events);

        // Read the post-batch projected snapshot off the *next* state
        // rather than `stateRef`, which can lag behind the dispatch
        // queue (apply_events commits inside a view transition).
        const projectedNext =
          resp.viewer_events.length > 0
            ? selectGameView(
                reduceBattle(stateRef.current, {
                  type: "apply_events",
                  events: resp.viewer_events,
                }),
              )
            : selectGameView(stateRef.current);
        const finalSnap = projectedNext;
        const surfacedOurTargetPicker =
          finalSnap?.pending?.kind === "target" &&
          finalSnap.pending.player === viewer;
        if (
          castEid != null &&
          !surfacedOurTargetPicker &&
          !eventsCommittedCast(resp.viewer_events, castEid)
        ) {
          // No-legal-targets path: engine refused the cast, card is
          // back in hand, the hover clone never paired with a battle
          // slot. Wipe it explicitly. Also drop the optimistic mana
          // deduction since no mana was spent.
          playSfx("ai_notice_or_warning_sound");
          abortPlayHover();
          setPendingPlay(null);
        }
        if (
          castEid != null &&
          surfacedOurTargetPicker &&
          finalSnap?.players[viewer].hand?.some(
            (c) => c.entity_id === castEid,
          )
        ) {
          // Spell-with-target: engine put the card back in hand
          // pending the picker. Hide the hand slot until the spell
          // actually resolves out of the hand.
          markCastingDissolved(castEid);
        }
        await awaitSpellFizzle();
      } catch (e) {
        playSfx("ai_error_sound");
        setError(e instanceof Error ? e.message : String(e));
        setPendingPlay(null);
      } finally {
        finishPlayHover();
      }
    },
    [
      cancelAnimation,
      gameId,
      playEvents,
      snapshot,
      viewer,
      spectating,
      animBoardRefs,
      beginPlayHover,
      abortPlayHover,
      markCastingDissolved,
      awaitSpellFizzle,
      finishPlayHover,
    ],
  );

  // Cold-start only — used after a 404 / channel join. Steady-state
  // mutations flow through `applyEngineStep` (seq-deduped); refetching
  // a snapshot mid-game would skip the event log and SFX baseline.
  //
  // For spectators we ALSO use this path on every channel `steps`
  // push: actor-side step snapshots are computed for the non-actor
  // player's seat, but a spectator may have picked the opposite
  // perspective. Refetching with our own `viewer_seat` keeps the
  // perspective consistent at the cost of skipping inter-step
  // animations.
  const refetchOnReconnect = useCallback(async () => {
    if (!gameId) return;
    try {
      const next = await fetchStateAny(
        spectating ? { viewer_seat: spectatorPerspective } : undefined,
      );
      dispatch({ type: "loaded", snapshot: next });
      setWrapper({
        seat: next.seat,
        room_id: next.room_id,
        ai_persona: next.ai_persona,
        human_persona: next.human_persona,
        spectating: next.spectating,
        seat_personas: next.seat_personas,
        opponent_connected: next.opponent_connected,
        idle_seconds: next.idle_seconds,
      });
    } catch (e) {
      if (isNotFoundError(e)) {
        // Spectators: the match may have ended and the engine
        // session deleted. Don't navigate away — the user is
        // mid-match-view; the `game_ended` channel push will hand
        // us a synthesized terminal result so the banner can
        // render. Players get the legacy navigate-away behavior.
        if (spectating) return;
        navigate(roomId ? `/room/${roomId}` : "/lobby", { replace: true });
      }
    }
  }, [gameId, fetchStateAny, navigate, roomId, spectating, spectatorPerspective]);

  // Game channel is keyed by engine session id (not game id).
  // Players resolve it via `/api/games` (their active match row).
  // Spectators don't have an active-match row (they aren't in
  // `Doi.Match.UserIndex`), so we fall back to the URL `gameId`,
  // which RoomPage stamps as the engine session id for the
  // spectator navigation.
  const [engineSessionId, setEngineSessionId] = useState<string | null>(null);
  useEffect(() => {
    if (!gameId || engineSessionId) return;
    if (spectating) {
      setEngineSessionId(gameId);
      return;
    }
    void (async () => {
      try {
        const games = await api.getActiveGame();
        if (games.active && String(games.active.id) === String(gameId)) {
          setEngineSessionId(games.active.engine_session_id);
          return;
        }
        // No active match behind this URL (dead-session deep link) —
        // the HTTP load 404s and `handleLoadError` redirects to
        // /lobby, exactly like the pre-channel mount load did.
        void loadViaHttp();
      } catch {
        // Lookup failed (transient / 401) — load over HTTP so the
        // page still renders or surfaces the right error.
        void loadViaHttp();
      }
    })();
  }, [gameId, engineSessionId, spectating, loadViaHttp]);

  // The join effect below must not churn (leave + rejoin + reload)
  // every time one of its callbacks closes over fresh state, so it
  // reads them through a ref that always tracks the latest render.
  const liveChannelRef = useRef({
    playEvents,
    refetchOnReconnect,
    viewer,
    applyLoadedSnapshot,
    handleLoadError,
    loadViaHttp,
  });
  useEffect(() => {
    liveChannelRef.current = {
      playEvents,
      refetchOnReconnect,
      viewer,
      applyLoadedSnapshot,
      handleLoadError,
      loadViaHttp,
    };
  });

  // Join `game:<engineSessionId>` for every match — PvP *and*
  // single-player. The channel carries the opponent's event pushes
  // (PvP), the `game_ended` teardown ping, and — since the A2 latency
  // work — the viewer's own `action` / `state` requests, replacing
  // the per-action HTTP round-trip. The initial snapshot also loads
  // through here (a `state` request right after the join) with
  // `loadViaHttp` as the fallback when the channel is unavailable.
  useEffect(() => {
    if (!engineSessionId) return;
    let cancelled = false;
    let conn: GameChannelHandle | null = null;

    void (async () => {
      try {
        const handle = await joinGameChannel(
          engineSessionId,
          {
            onEvents: ({ events, first_seq }) => {
              if (cancelled) return;
              const live = liveChannelRef.current;
              if (spectating) {
                // Spectator path: actor-side event streams are scoped
                // to the OTHER seat. Refetch the snapshot for our own
                // perspective rather than apply events that may
                // reveal hidden hand cards from the wrong angle.
                void live.refetchOnReconnect();
                return;
              }
              if (events.length === 0) return;
              // Gap-detect: every event from the server is consecutive
              // per seat. A mismatch against `lastEventSeq` means we
              // missed a frame (channel disconnect, server restart) —
              // resync via a full state fetch rather than apply a
              // stale batch onto a missing prefix.
              const cur = stateRef.current;
              if (first_seq !== cur.lastEventSeq + 1) {
                void live.refetchOnReconnect();
                return;
              }
              void live.playEvents(events);
            },
            onGameEnded: ({ outcome, result, reason }) => {
              if (cancelled) return;
              if (stateRef.current.terminal) return;
              const live = liveChannelRef.current;
              // No-winner abandonment (idle eviction, superseded by a
              // new battle, or the server couldn't restore the engine
              // session). Park on the ErrorScreen with a reason-specific
              // message rather than silently refetching into a 404 →
              // /lobby bounce. Applies to players and spectators alike.
              if (reason && !outcome && !result) {
                animationAbortRef.current?.abort();
                playSfx("ai_harp_strum_sad");
                setError(reason);
                return;
              }
              if (spectating) {
                // Spectators have no self-outcome; the server's
                // `result` field carries the seat-level verdict so we
                // can synthesize a terminal banner without refetching
                // the (now-deleted) engine session.
                if (result) {
                  animationAbortRef.current?.abort();
                  dispatch({ type: "set_terminal_result", result });
                  playSfx("ai_harp_strum_sad");
                }
                // No `result` (older servers, or end_game without a
                // terminal verdict): stay on the current snapshot.
                // The user can hit LEAVE manually.
                return;
              }
              if (outcome) {
                // Concede / claim-victory path: engine didn't declare
                // a result on this side; the server pushed our own
                // outcome over the channel. Synthesize the result so
                // racing engine steps get dropped.
                animationAbortRef.current?.abort();
                dispatch({
                  type: "opponent_ended",
                  viewer: live.viewer,
                  outcome,
                });
                playSfx(
                  outcome === "win"
                    ? "ai_happy_harp_strum"
                    : "ai_harp_strum_sad",
                );
                return;
              }
              void live.refetchOnReconnect();
            },
          },
          spectating ? { viewerSeat: spectatorPerspective } : undefined,
        );
        if (cancelled) {
          handle.leave();
          return;
        }
        conn = handle;
        gameConnRef.current = handle;
        // Initial snapshot over the channel — replaces the old HTTP
        // mount load (and re-runs on spectator perspective flips,
        // which re-key this effect).
        try {
          const snap = await handle.fetchState(
            spectating ? { viewer_seat: spectatorPerspective } : undefined,
          );
          if (!cancelled) liveChannelRef.current.applyLoadedSnapshot(snap);
        } catch (e) {
          if (!cancelled) liveChannelRef.current.handleLoadError(e);
        }
      } catch {
        // Channel unavailable (socket down, join refused/timed out) —
        // play the whole match over the HTTP routes instead.
        // `gameConnRef` stays null so `submitAction` / `fetchStateAny`
        // use their fallbacks.
        if (!cancelled) void liveChannelRef.current.loadViaHttp();
      }
    })();

    return () => {
      cancelled = true;
      if (conn) {
        if (gameConnRef.current === conn) gameConnRef.current = null;
        conn.leave();
      }
    };
  }, [engineSessionId, spectating, spectatorPerspective]);

  // PvP claim-victory tracker. Polls 5s for `idle_seconds` and
  // `opponent_connected`; everything else flows through the channel.
  const [opponentIdleSeconds, setOpponentIdleSeconds] = useState<number>(0);
  const [opponentConnected, setOpponentConnected] = useState<boolean>(true);
  useEffect(() => {
    if (!gameId || !roomId) return;
    // Spectators don't have a claim-victory affordance — skip the
    // poll entirely.
    if (spectating) return;
    if (snapshot?.result) return;
    const pending = snapshot?.pending;
    if (!pending || pending.player === viewer) {
      setOpponentIdleSeconds(0);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await fetchStateAny();
        if (cancelled) return;
        setOpponentIdleSeconds(next.idle_seconds ?? 0);
        setOpponentConnected(next.opponent_connected ?? true);
      } catch {
        // ignore
      }
    };
    void tick();
    const handle = window.setInterval(tick, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [gameId, roomId, spectating, snapshot?.pending, snapshot?.result, viewer, fetchStateAny]);

  // Mirrors `Doi.Battles.claim_victory_thresholds/0` server-side.
  const DISCONNECTED_THRESHOLD_S = 120;
  const CONNECTED_THRESHOLD_S = 300;

  const activeThresholdSeconds = opponentConnected
    ? CONNECTED_THRESHOLD_S
    : DISCONNECTED_THRESHOLD_S;
  const remainingSeconds = Math.max(
    0,
    activeThresholdSeconds - opponentIdleSeconds,
  );

  const opponentsTurn =
    !!snapshot?.pending && snapshot.pending.player !== viewer && !snapshot.result;
  // Field-mute flag tracks whose TURN it is, not whose pending decision
  // is current. Using pending here makes the field flip back to full
  // colour for the viewer's blocker-pick mid-opponent-turn, then back
  // to muted when the engine resumes — which the user sees as the
  // board "randomly flickering between full colour and grey". Anchor
  // to `active_player` instead so opp turns stay consistently dimmed.
  const fieldMuted = !!snapshot && snapshot.active_player !== viewer;

  const claimStatus: { message: string } | null = (() => {
    if (!roomId || !opponentsTurn) return null;
    if (remainingSeconds === 0) return null;
    if (!opponentConnected) {
      return {
        message: `Opponent disconnected — ${formatSeconds(remainingSeconds)} until you can claim victory`,
      };
    }
    // "Taking too long" warning only in the last 2 min of connected timer.
    const TAKING_TOO_LONG_LEAD_S = 120;
    if (remainingSeconds <= TAKING_TOO_LONG_LEAD_S) {
      return {
        message: `Opponent taking too long — ${formatSeconds(remainingSeconds)} until you can claim victory`,
      };
    }
    return null;
  })();

  const canClaimVictory = !!roomId && opponentsTurn && remainingSeconds === 0;

  const onClaimVictory = useCallback(async () => {
    if (!gameId) return;
    try {
      await api.claimVictory(gameId);
      // Belt-and-suspenders navigate in case the `game_ended` channel
      // push that drives the win banner gets dropped.
      animationAbortRef.current?.abort();
      navigate(roomId ? `/room/${roomId}` : "/lobby", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [gameId, navigate, roomId]);

  // Drives both the header CONCEDE button (mid-match) and the
  // GameOverBanner's bottom button (terminal). Dispatches `conceded`
  // *before* the network round-trip so racing engine steps get
  // dropped by the reducer's terminal lock.
  //
  // Mid-match concede: dispatch terminal, fire endGame in the
  // background, and *leave the banner up* so the player can read the
  // result before they choose to return. The banner's bottom button
  // runs through this same callback — but by then `terminal` is true
  // and we take the `goBack` branch.
  //
  // Without this two-step shape, a mid-match concede dispatch + an
  // immediate `goBack` would unmount the banner before the user saw it.
  const onConcede = useCallback(async () => {
    // Spectator exit. The "RETURN TO ROOM" affordance from the
    // post-game GameOverBanner and the mid-match "LEAVE" both flow
    // through here. Both navigate back to /room/<id> when one
    // exists, carrying `state.fromSpectator: true` so RoomPage knows
    // to skip its "in_game → auto-bounce-to-spectate" rule (which
    // would otherwise loop the spectator straight back here). Fire
    // endGame fire-and-forget so the server drops them from the
    // match's spectator set.
    if (spectating) {
      animationAbortRef.current?.abort();
      if (gameId) {
        void api.endGame(gameId).catch(() => {});
      }
      if (roomId) {
        navigate(`/room/${roomId}`, {
          replace: true,
          state: { fromSpectator: true },
        });
      } else {
        navigate("/lobby", { replace: true });
      }
      return;
    }
    // Challenge battles route back to the NPC's page; PvP back to
    // the room; everything else to /lobby. When the route lands on
    // an NPC page (`challengeReturnTo`), we forward the server's
    // author-declared `resume_node` (captured into
    // `challengeResumeNodeId` when the engine reported game-over)
    // as `location.state.resumeNodeId`. Null = "let the NPC page's
    // normal default-node resolution pick" (Pip-style between-round
    // overrides).
    const dest = challengeReturnTo ?? (roomId ? `/room/${roomId}` : "/lobby");
    const goBack = (resumeNodeId: string | null) => {
      if (challengeReturnTo) {
        navigate(dest, { replace: true, state: { resumeNodeId } });
      } else {
        navigate(dest, { replace: true });
      }
    };

    if (!gameId || stateRef.current.terminal) {
      // Already terminal — this is the banner's bottom button or a
      // natural game-over path. The resume node was captured either
      // when the engine reported terminal or by the endGame call below.
      goBack(challengeResumeNodeId);
      return;
    }
    animationAbortRef.current?.abort();
    dispatch({ type: "conceded", loserSeat: viewer });
    playSfx("ai_harp_strum_sad");
    // Fire-and-forget endGame; we don't `goBack` here because we want
    // the GameOverBanner (rendered off the dispatched terminal state)
    // to stay visible until the user clicks its bottom button.
    void (async () => {
      try {
        const r = await api.endGame(gameId);
        if (r.challenge?.resume_node !== undefined) {
          setChallengeResumeNodeId(r.challenge.resume_node);
        }
      } catch {
        // Engine already dropped the session — local terminal dispatch
        // keeps the banner up regardless.
      }
    })();
  }, [challengeResumeNodeId, challengeReturnTo, gameId, navigate, roomId, spectating, viewer]);

  const onInspect = useCallback(
    (
      card: CardView,
      _zone: "hand" | "battle",
      handIdx: number | null,
      opts: { muted?: boolean } = {},
    ) => {
      // An off-limits combat click still opens the preview, but warns
      // instead of chiming and suppresses the card's green ring.
      playSfx(opts.muted ? "ai_notice_or_warning_sound" : "ai_paper_flutter");
      setSelection((s) => {
        if (s.inspected?.entity_id === card.entity_id && s.handIdx === handIdx) {
          return { ...s, inspected: null, handIdx: null, inspectRingMuted: false };
        }
        return { ...s, inspected: card, handIdx, inspectRingMuted: !!opts.muted };
      });
    },
    [],
  );

  // Click on a card span in the event log. Prefers a live CardView
  // (so the preview reflects current stats / damage / face-down) and
  // falls back to owned-catalog static data when the card has left play.
  const onInspectCardFromLog = useCallback(
    (eid: number | null, defId: string, name: string) => {
      if (snapshot && eid != null) {
        for (const seat of snapshot.players) {
          const inHand = seat.hand?.find((c) => c.entity_id === eid);
          if (inHand) {
            setSelection((s) => ({ ...s, inspected: inHand, handIdx: null }));
            return;
          }
          const inBattle = seat.battle.find((c) => c.entity_id === eid);
          if (inBattle) {
            setSelection((s) => ({ ...s, inspected: inBattle, handIdx: null }));
            return;
          }
          // Once a card leaves battle, its CardView sits in graveyard /
          // exile. The previous code stopped at battle and forced an
          // async by-id fetch (showing "Loading…") for any card that
          // had already died or been exiled — including ones the SPA
          // had every field for. Check the public-zone arrays before
          // falling back to the cached def / network path.
          const inGraveyard = seat.graveyard?.find((c) => c.entity_id === eid);
          if (inGraveyard) {
            setSelection((s) => ({ ...s, inspected: inGraveyard, handIdx: null }));
            return;
          }
          const inExile = seat.exile?.find((c) => c.entity_id === eid);
          if (inExile) {
            setSelection((s) => ({ ...s, inspected: inExile, handIdx: null }));
            return;
          }
        }
      }
      // Even with no live CardView, the v2 wire's Reveal event already
      // populated `state.defs` with this card's EntityDef the moment
      // it became visible. Build a CardView from the cached def so the
      // preview lands instantly without an HTTP round-trip — the only
      // way to fall through to the async fetch below is a def the
      // viewer has never been revealed, which can't appear in the
      // event log.
      const cachedDef = state.defs.get(defId);
      if (cachedDef) {
        setSelection((s) => ({
          ...s,
          inspected: {
            entity_id: 0,
            def_id: cachedDef.def_id,
            name: cachedDef.name,
            type: cachedDef.type,
            cost: cachedDef.cost,
            base_attack: cachedDef.base_attack,
            base_life: cachedDef.base_life,
            current_attack: cachedDef.base_attack,
            current_life: cachedDef.base_life,
            damage_marked: 0,
            remaining_life: cachedDef.base_life,
            can_attack: false,
            is_attacking: false,
            has_script: cachedDef.has_script,
            flavor: cachedDef.flavor ?? null,
            rules_text: cachedDef.rules_text ?? null,
            image_url:
              cachedDef.image_url ?? `/card-images/${cachedDef.def_id}`,
            faction: cachedDef.faction ?? "humans",
            race: cachedDef.race ?? null,
            class: cachedDef.class ?? null,
            expansion_set: cachedDef.expansion_set ?? null,
            rarity: cachedDef.rarity ?? null,
            summon_only: cachedDef.summon_only,
            seal_counters: 0,
            face_down: false,
            keywords: [],
          },
          handIdx: null,
        }));
        return;
      }
      const fromOwned = catalogById.get(defId);
      if (fromOwned) {
        setSelection((s) => ({
          ...s,
          inspected: catalogToCardView(fromOwned),
          handIdx: null,
        }));
        return;
      }
      const cached = opponentCardCache.get(defId);
      if (cached) {
        setSelection((s) => ({
          ...s,
          inspected: catalogToCardView(cached),
          handIdx: null,
        }));
        return;
      }
      // Optimistic placeholder while we fetch by-id; type "spell" so
      // the Card doesn't show fake 0/0 stats during the request.
      //
      // The placeholder MUST carry a real faction — `factionFor` is
      // strict (there is no neutral faction in this game) and a null
      // faction would crash the Card render the moment the inspector
      // tries to display this placeholder. We harvest a faction from
      // any visible card in the snapshot: opponent's first, since
      // most log-clicks are about the opponent's plays; viewer's
      // own as a fallback. Replaced by the real faction once
      // `getCardById` returns.
      const placeholderFaction = pickAnyVisibleFaction(snapshot, viewer);
      setSelection((s) => ({
        ...s,
        inspected: {
          entity_id: 0,
          def_id: defId,
          name,
          type: "spell",
          cost: 0,
          base_attack: 0,
          base_life: 0,
          current_attack: 0,
          current_life: 0,
          damage_marked: 0,
          remaining_life: 0,
          can_attack: false,
          is_attacking: false,
          has_script: false,
          flavor: null,
          rules_text: "Loading…",
          image_url: defId.length > 0 ? `/card-images/${defId}` : null,
          faction: placeholderFaction,
          expansion_set: null,
          rarity: null,
          summon_only: false,
          seal_counters: 0,
          face_down: false,
          keywords: [],
        },
        handIdx: null,
      }));
      void (async () => {
        try {
          const fetched = await api.getCardById(defId);
          setOpponentCardCache((m) => {
            if (m.has(defId)) return m;
            const next = new Map(m);
            next.set(defId, fetched);
            return next;
          });
          setSelection((s) =>
            // Only replace if the user is still inspecting the same card.
            s.inspected?.def_id === defId && s.inspected?.entity_id === 0
              ? { ...s, inspected: catalogToCardView(fetched) }
              : s,
          );
        } catch {
          // 404 / network: leave the placeholder up.
        }
      })();
    },
    [snapshot, catalogById, opponentCardCache, state.defs],
  );

  const onToggleAttacker = useCallback(
    (card: CardView) => {
      // PvP gate: only the seat the engine is waiting on can mark attackers.
      // Spectators are read-only even when their chosen perspective
      // happens to be the active player.
      if (spectating) return;
      const pend = snapshot?.pending;
      if (pend?.kind !== "attackers" || pend.player !== viewer) return;
      // Reckless creatures attack regardless of player input — the
      // engine forces them. Clicking is a no-op so the UI doesn't
      // pretend the player can opt them out.
      if (hasReckless(card)) return;
      setSelection((s) => {
        const next = new Set(s.attackers);
        if (next.has(card.entity_id)) {
          next.delete(card.entity_id);
          playSfx("ai_sword_being_sheathed");
        } else {
          next.add(card.entity_id);
          playSfx("ai_sword_unsheath");
        }
        return { ...s, attackers: next };
      });
    },
    [snapshot?.pending, viewer, spectating],
  );

  const onOwnBlockerClick = useCallback((card: CardView) => {
    // Mirrors the engine's per-attacker veto list so non-blockers
    // give immediate visual feedback (engine is still authoritative
    // at submit time).
    if (spectating) return;
    const pend = snapshot?.pending;
    if (pend?.kind === "blocks" && pend.player !== viewer) return;
    if (pend?.kind === "blocks") {
      if (!canBlockAny(card, pend.attackers, pend.illegal_blockers)) return;
    } else if (card.type !== "creature") {
      return;
    }
    setSelection((s) => {
      for (const [att, bs] of s.blocks) {
        if (bs.has(card.entity_id)) {
          const next = new Map(s.blocks);
          const nextSet = new Set(bs);
          nextSet.delete(card.entity_id);
          if (nextSet.size === 0) next.delete(att);
          else next.set(att, nextSet);
          return { ...s, blocks: next, pendingBlocker: card.entity_id };
        }
      }
      return {
        ...s,
        pendingBlocker: s.pendingBlocker === card.entity_id ? null : card.entity_id,
      };
    });
  }, [snapshot?.pending, viewer, spectating]);

  const onCancelPendingBlocker = useCallback(() => {
    setSelection((s) => (s.pendingBlocker === null ? s : { ...s, pendingBlocker: null }));
  }, []);

  const onEnemyAttackerClick = useCallback(
    (card: CardView) => {
      if (spectating) return;
      const pendingNow = snapshot?.pending;
      if (pendingNow?.kind === "blocks" && pendingNow.player !== viewer) return;
      setSelection((s) => {
        if (s.pendingBlocker === null) return s;
        // Per-pair legality gate, mirroring the engine's veto list.
        const pend = snapshot?.pending;
        if (pend?.kind === "blocks") {
          const you = snapshot?.players?.[viewer];
          const blocker = you?.battle.find((c) => c.entity_id === s.pendingBlocker);
          if (
            blocker &&
            !isBlockPairLegal(blocker, card.entity_id, pend.illegal_blockers)
          ) {
            return { ...s, pendingBlocker: null };
          }
        }
        const blocks = new Map(s.blocks);
        const prev = blocks.get(card.entity_id) ?? new Set<number>();
        const next = new Set(prev);
        next.add(s.pendingBlocker);
        blocks.set(card.entity_id, next);
        return { ...s, blocks, pendingBlocker: null };
      });
    },
    [snapshot, spectating, viewer],
  );

  // Drag-to-block window listeners. Reads fresh state via
  // `liveDragRef.current` so we don't re-subscribe on every render.
  useEffect(() => {
    const move = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d || d.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (dx * dx + dy * dy < 25) return;
      d.dragging = true;
      const live = liveDragRef.current;
      if (live.you && live.pendingBlocker !== d.blockerId) {
        const card = live.you.battle.find((c) => c.entity_id === d.blockerId);
        if (card) live.onOwnBlockerClick(card);
      }
    };
    const up = (e: MouseEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      if (!d || !d.dragging) return;
      suppressNextClickRef.current = true;
      const live = liveDragRef.current;
      if (!live.opponent) return;
      const attackerId = findIncomingAttackerAt(
        e.clientX,
        e.clientY,
        cardRefs,
        live.incomingAttackers,
      );
      if (attackerId !== null) {
        const attacker = live.opponent.battle.find((c) => c.entity_id === attackerId);
        if (attacker) live.onEnemyAttackerClick(attacker);
      } else {
        live.onCancelPendingBlocker();
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [cardRefs]);

  // While a pendingBlocker is dangling, any click that doesn't land on
  // a card cancels it, and Escape always cancels.
  useEffect(() => {
    if (selection.pendingBlocker === null) return;
    const click = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) {
        onCancelPendingBlocker();
        return;
      }
      if (target.closest('[data-card-tile="true"]')) return;
      onCancelPendingBlocker();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancelPendingBlocker();
    };
    document.addEventListener("click", click);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("click", click);
      document.removeEventListener("keydown", key);
    };
  }, [selection.pendingBlocker, onCancelPendingBlocker]);

  const onToggleTarget = useCallback((ref: TargetRef) => {
    setSelection((s) => {
      const key = targetKey(ref);
      const next = new Set(s.targetPicks);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...s, targetPicks: next };
    });
  }, []);

  const onClearTargets = useCallback(() => {
    setSelection((s) => ({ ...s, targetPicks: new Set() }));
  }, []);

  const targeting: Targeting | undefined = useMemo(() => {
    if (!snapshot) return undefined;
    // Spectators never get a target picker — the action would 403
    // server-side and the candidate-ring overlay would just confuse
    // the audience.
    if (spectating) return undefined;
    if (snapshot.pending?.kind !== "target") return undefined;
    if (snapshot.pending.player !== viewer) return undefined;
    const req = snapshot.pending.request;
    const candidateEntities = new Set<number>();
    const candidatePlayers = new Set<PlayerId>();
    for (const c of req.candidates) {
      if (c.kind === "entity") candidateEntities.add(c.id);
      else candidatePlayers.add(c.id);
    }
    // Map iteration is insertion-ordered → drives the 1-based "pick #" badge.
    const pickedEntities = new Map<number, number>();
    const pickedPlayers = new Map<PlayerId, number>();
    let order = 0;
    for (const key of selection.targetPicks) {
      const ref = parseTargetKey(key);
      order += 1;
      if (ref.kind === "entity") pickedEntities.set(ref.id, order);
      else pickedPlayers.set(ref.id, order);
    }
    const picks: TargetRef[] = Array.from(selection.targetPicks, (k) =>
      parseTargetKey(k),
    );
    return {
      request: req,
      candidateEntities,
      candidatePlayers,
      pickedEntities,
      pickedPlayers,
      onToggle: onToggleTarget,
      onConfirm: () => {
        submitAction({ kind: "target", picks });
      },
      onClear: onClearTargets,
    };
  }, [snapshot, selection.targetPicks, onToggleTarget, onClearTargets, submitAction]);

  // Which off-board zones contain target candidates (highlights the
  // indicator on the player panel and wires the ZoneViewer pick mode).
  const zoneCandidacy = useMemo(() => {
    const empty = {
      deck: new Set<number>(),
      graveyard: new Set<number>(),
      exile: new Set<number>(),
      opp_graveyard: new Set<number>(),
      opp_exile: new Set<number>(),
    };
    if (!snapshot || !targeting) return empty;
    const own = snapshot.players[viewer];
    const opp = snapshot.players[1 - viewer];
    const deckIds = new Set(own.deck?.map((c) => c.entity_id) ?? []);
    const graveIds = new Set(own.graveyard?.map((c) => c.entity_id) ?? []);
    const exileIds = new Set(own.exile?.map((c) => c.entity_id) ?? []);
    const oppGraveIds = new Set(opp.graveyard?.map((c) => c.entity_id) ?? []);
    const oppExileIds = new Set(opp.exile?.map((c) => c.entity_id) ?? []);
    const out = {
      deck: new Set<number>(),
      graveyard: new Set<number>(),
      exile: new Set<number>(),
      opp_graveyard: new Set<number>(),
      opp_exile: new Set<number>(),
    };
    for (const eid of targeting.candidateEntities) {
      if (deckIds.has(eid)) out.deck.add(eid);
      if (graveIds.has(eid)) out.graveyard.add(eid);
      if (exileIds.has(eid)) out.exile.add(eid);
      if (oppGraveIds.has(eid)) out.opp_graveyard.add(eid);
      if (oppExileIds.has(eid)) out.opp_exile.add(eid);
    }
    return out;
  }, [snapshot, targeting]);

  // Private "reveal" payload (peek / scry / look-at-opponent's-hand).
  // The v2 wire ships these as `revealed_eids: number[]`; resolve each
  // eid against the projected snapshot's battle / hand / graveyard /
  // exile rows so the reveal UI gets a fully-render-ready `CardView`.
  const revealedCards = useMemo<CardView[]>(() => {
    if (!snapshot || snapshot.pending?.kind !== "target") return [];
    if (snapshot.pending.player !== viewer) return [];
    const eids = snapshot.pending.revealed_eids ?? [];
    if (eids.length === 0) return [];
    const byEid = new Map<number, CardView>();
    for (const seat of snapshot.players) {
      for (const c of seat.battle) byEid.set(c.entity_id, c);
      for (const c of seat.hand ?? []) byEid.set(c.entity_id, c);
      for (const c of seat.graveyard ?? []) byEid.set(c.entity_id, c);
      for (const c of seat.exile ?? []) byEid.set(c.entity_id, c);
      for (const c of seat.deck ?? []) byEid.set(c.entity_id, c);
    }
    const out: CardView[] = [];
    for (const eid of eids) {
      const c = byEid.get(eid);
      if (c) out.push(c);
    }
    return out;
  }, [snapshot]);

  useEffect(() => {
    const key = pendingKey(snapshot?.pending ?? null);
    if (revealedCards.length > 0 && openZone === null && dismissedRevealKey !== key) {
      setOpenZone("reveal");
    } else if (revealedCards.length === 0 && openZone === "reveal") {
      setOpenZone(null);
    }
  }, [revealedCards, openZone, snapshot?.pending, dismissedRevealKey]);

  const actionCtx: ActionContext | null = useMemo(() => {
    if (!snapshot) return null;
    const you = snapshot.players[viewer];
    const hasAttackableCreatures = you.battle.some(
      (c) => c.type === "creature" && c.can_attack,
    );
    return {
      pending: snapshot.pending,
      selection,
      mana: you.mana,
      handSize: you.hand_size,
      hand: you.hand ?? [],
      animating,
      gameOver: snapshot.result !== null,
      hasAttackableCreatures,
      viewer,
    };
  }, [snapshot, selection, animating, viewer]);

  const primary = useMemo(() => {
    if (!actionCtx) return null;
    // Spectators see the card preview (rules text, art, keywords) but
    // never the "PLAY n MANA" / "ATTACK" primary-action button — the
    // server would 403 the submit and the button wouldn't do anything.
    if (spectating) return null;
    return primaryAction(actionCtx, selection.inspected, selection.handIdx);
  }, [actionCtx, selection.inspected, selection.handIdx, spectating]);

  const endTurn = useMemo(() => {
    if (!actionCtx) return null;
    if (spectating) return null;
    return endTurnAction(actionCtx);
  }, [actionCtx, spectating]);

  if (error) {
    return (
      <ErrorScreen
        error={error}
        onBack={() => navigate(roomId ? `/room/${roomId}` : "/lobby")}
        theme={theme}
      />
    );
  }
  if (!snapshot || !actionCtx) {
    return <LoadingScreen theme={theme} />;
  }

  const you = snapshot.players[viewer];
  const hand = you.hand ?? [];
  const isMain =
    snapshot.pending?.kind === "main" && snapshot.pending.player === viewer;

  // Heuristic preflight for spells that target a creature. The
  // authoritative castable signal lives on `EntityRecord.castable`,
  // but the v2 translator doesn't compute it yet, so cards like
  // "Kill target creature" appear playable on an empty board, the
  // player clicks them, the engine rejects with NoValidTargets, and
  // the card snaps back to hand — wasted click + confusing UX.
  // Match on rules_text and gate by whether any creature exists on
  // either battlefield. Engine remains authoritative at submit.
  const noTargetHandEids: number[] = (() => {
    const opp = snapshot.players[1 - viewer];
    const anyCreatureOnBoard =
      you.battle.some((c) => c.type === "creature") ||
      opp.battle.some((c) => c.type === "creature");
    if (anyCreatureOnBoard) return [];
    const out: number[] = [];
    for (const card of hand) {
      if (card.type !== "spell") continue;
      const rules = card.rules_text ?? "";
      if (/\btarget\b[^.]*\bcreature\b/i.test(rules)) {
        out.push(card.entity_id);
      }
    }
    return out;
  })();

  const yourFaction = humanPersona?.faction ?? YOU.faction;
  const yourFactionColor = factionFor(yourFaction).color;
  // Discard actions are ruby; everything else uses the deck's faction color.
  const endTurnAccent =
    endTurn?.action?.kind === "discard"
      ? theme.accent
      : yourFactionColor;
  // The contextual button is only a live decision once the paced replay
  // has caught up to where the engine is actually waiting. Mid-animation
  // the displayed pending can be a phase the engine already moved past
  // (our attackers step while combat resolves into the opponent's turn),
  // so the button shows "…" and stays disabled until they agree.
  const animationSettled = actionButtonSettled(
    animating,
    liveTargetRef.current,
    pendingKey(snapshot.pending, snapshot.turn),
  );

  const opponent = snapshot.players[1 - viewer];
  const opponentName = aiPersona?.name ?? OPPONENT.name;
  const opponentFaction = aiPersona?.faction ?? OPPONENT.faction;
  const opponentCity = aiPersona?.city ?? null;
  const youName = humanPersona?.name ?? YOU.name;
  const youSubtitle = humanPersona?.deck_name ?? null;
  const pending = snapshot.pending;
  const incomingAttackers = new Set(
    pending?.kind === "blocks" ? pending.attackers : [],
  );

  // With a blocker pinned, dim the opponent attackers it can't legally
  // pair with so valid drop targets read at a glance.
  const unavailableOppAttackers = new Set<number>();
  if (
    pending?.kind === "blocks" &&
    pending.player === viewer &&
    selection.pendingBlocker !== null
  ) {
    const blocker = you.battle.find((c) => c.entity_id === selection.pendingBlocker);
    if (blocker) {
      for (const aid of pending.attackers) {
        if (!isBlockPairLegal(blocker, aid, pending.illegal_blockers)) {
          unavailableOppAttackers.add(aid);
        }
      }
    }
  }

  // Gate selection-driven combat decoration on the current pending kind.
  // selection.attackers / selection.blocks are local mirrors of an
  // in-flight choice; once the engine has moved past the relevant
  // sub-phase, those sets must not paint stale red/yellow rings even if
  // resetSelectionIfPendingChanged hasn't fired yet (it runs at the
  // end of an animation batch — intermediate frames need a guard).
  //
  // `snapshot.combat == null` closes the attacker-pick layer the moment
  // the engine commits the declaration: `pending` alone is not enough,
  // because after "declare attackers" the next pending decision can be
  // a whole opponent turn away (the engine resolves combat, ends the
  // turn, and plays the AI's turn before the next decision lands), so
  // `pending` stays stale-`attackers` for that entire stream. The
  // reducer sets `combat` on `attackers_declared` and the committed
  // attackers' entity-level `is_attacking` flags take over the red
  // ring + lift (Field.tsx) until a `phase` event leaves combat and
  // clears both.
  const showingAttackerPicks =
    pending?.kind === "attackers" &&
    pending.player === viewer &&
    snapshot.combat == null;
  const showingBlockPicks =
    pending?.kind === "blocks" && pending.player === viewer;
  const displayedAttackers = showingAttackerPicks
    ? selection.attackers
    : EMPTY_NUMBER_SET;
  const displayedBlocks = showingBlockPicks
    ? selection.blocks
    : EMPTY_BLOCK_MAP;
  // Green = "this card is shown in the right sidebar". We suppress it
  // project-wide in two cases: during target mode (the amber target
  // ring is doing the work), and when the inspected card was opened by
  // an off-limits combat click (`inspectRingMuted`) — the preview still
  // shows it, but a card you can't act on must not read as selected.
  // Suppression is field-wide but only the one inspected card matches
  // `inspectedId`, so it never dims an unrelated card.
  const suppressInspectRing = targeting != null || selection.inspectRingMuted;

  const ownInteraction: FieldInteraction = {
    inspectedId: selection.inspected?.entity_id ?? null,
    attackers: displayedAttackers,
    incomingAttackers: new Set(),
    activeAttacker: null,
    blocks: displayedBlocks,
    unavailable: unavailableOwnSet(you.battle, pending, viewer),
    targetCandidates: targeting?.candidateEntities,
    targetPicks: targeting?.pickedEntities,
    suppressInspectRing,
    onCardClick: (card) => {
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }
      // Targeting bounces through `targeting.onToggle` which submits
      // its picks via `submitAction` (server roundtrip). That path
      // must respect `animating` — otherwise a target pick lands
      // mid-event-flush and the engine sees the action against a
      // stale snapshot.
      if (targeting) {
        onInspect(card, "battle", null);
        if (animating) return;
        if (targeting.candidateEntities.has(card.entity_id)) {
          targeting.onToggle({ kind: "entity", id: card.entity_id });
        } else {
          playSfx("ai_notice_or_warning_sound");
        }
        return;
      }
      const blocksPending = pending?.kind === "blocks" ? pending : null;
      const intent = ownCardClickIntent({
        showingAttackerPicks,
        showingBlockPicks,
        canAttack: !!card.can_attack,
        canBlock:
          blocksPending != null &&
          canBlockAny(card, blocksPending.attackers, blocksPending.illegal_blockers),
      });
      // An off-limits card opens in the preview but warns and gets no
      // green ring — clicking your own enchantment or a summoning-sick
      // creature can't mark it, so it must not read as a selection.
      if (intent === "muted") {
        onInspect(card, "battle", null, { muted: true });
        return;
      }
      onInspect(card, "battle", null);
      // Attacker / blocker toggles only mutate the SPA's local
      // `selection` set — no server call, no event stream — so they
      // are safe to run inside the post-action pacing tail. Guarding
      // them on `animating` was the user-visible cause of "I clicked
      // my Rush troop right after summoning it and nothing happened":
      // the card opened in the inspector but the orange attacker border
      // never appeared because the click landed during the 300 ms
      // `MIN_STEP_DELAY_MS` settle window.
      if (intent === "attack") {
        onToggleAttacker(card);
      } else if (intent === "block-own") {
        onOwnBlockerClick(card);
      }
    },
    onCardMouseDown: (card, ev) => {
      if (pending?.kind !== "blocks" || pending.player !== viewer || animating) return;
      if (!canBlockAny(card, pending.attackers, pending.illegal_blockers)) return;
      dragRef.current = {
        blockerId: card.entity_id,
        startX: ev.clientX,
        startY: ev.clientY,
        dragging: false,
      };
    },
  };

  const oppInteraction: FieldInteraction = {
    inspectedId: selection.inspected?.entity_id ?? null,
    attackers: new Set(),
    incomingAttackers,
    activeAttacker: null,
    blocks: displayedBlocks,
    unavailable: unavailableOppAttackers,
    targetCandidates: targeting?.candidateEntities,
    targetPicks: targeting?.pickedEntities,
    suppressInspectRing,
    onCardClick: (card) => {
      // Target-picking keeps its own behavior: inspect, then toggle a
      // candidate or warn on a non-candidate.
      if (targeting) {
        onInspect(card, "battle", null);
        if (animating) return;
        if (targeting.candidateEntities.has(card.entity_id)) {
          targeting.onToggle({ kind: "entity", id: card.entity_id });
        } else {
          playSfx("ai_notice_or_warning_sound");
        }
        return;
      }
      const intent = opponentCardClickIntent({
        showingAttackerPicks,
        showingBlockPicks,
        isIncomingAttacker: incomingAttackers.has(card.entity_id),
      });
      // An off-limits card opens in the preview but warns and gets no
      // green ring — during your attack step the opponent's board is
      // untouchable, and during your block step only the creatures
      // actually attacking you can be clicked.
      if (intent === "muted") {
        onInspect(card, "battle", null, { muted: true });
        return;
      }
      onInspect(card, "battle", null);
      if (intent === "block-enemy") {
        if (animating) return;
        onEnemyAttackerClick(card);
      }
    },
  };

  const linePairs = flattenPairs(selection.blocks);

  liveDragRef.current = {
    pending,
    you,
    opponent,
    incomingAttackers,
    pendingBlocker: selection.pendingBlocker,
    onOwnBlockerClick,
    onEnemyAttackerClick,
    onCancelPendingBlocker,
  };

  // Spectators never see the blocker-selection UI; they're not the
  // ones declaring blocks, even when their chosen perspective is the
  // seat the engine is waiting on.
  const blocksPhaseForViewer =
    !spectating && pending?.kind === "blocks" && pending.player === viewer;
  const showLogInsteadOfPreview =
    layout.swap && selection.inspected == null;
  const showPreviewOnly =
    layout.swap && selection.inspected != null;

  // Compact embeds preview + log inside a single shared panelStyle
  // wrapper, so the inner components must skip their own border.
  const previewSlot = (
    <PreviewPanel
      card={selection.inspected}
      theme={theme}
      primary={primary}
      onPrimary={() => {
        if (primary?.action) submitAction(primary.action);
      }}
      embedded={layout.tier === "compact"}
    />
  );
  const logSlot = (
    <EventLog
      entries={eventLog}
      viewer={viewer}
      theme={theme}
      onInspectCard={onInspectCardFromLog}
      embedded={layout.tier === "compact"}
    />
  );

  // Per-tier grid wiring. Compact replaces the dual oppinfo/youinfo
  // cells with a single `rail` column spanning both field rows, and
  // drops the phase chip row entirely.
  const isCompact = layout.tier === "compact";
  const gridConfig = isCompact
    ? {
        columns: "80px minmax(0, 1fr) 280px",
        rows: "auto 1fr 1fr auto",
        areas: `
          "header  header    header"
          "rail    oppfield  sidebar"
          "rail    youfield  sidebar"
          ".       hand      ."
        `,
        columnGap: 8,
        rowGap: 4,
      }
    : {
        columns: "190px minmax(0, 1fr) 340px",
        rows: "auto 1fr auto 1fr auto",
        areas: `
          "header   header    header"
          "oppinfo  oppfield  sidebar"
          ".        phase     sidebar"
          "youinfo  youfield  sidebar"
          ".        hand      ."
        `,
        columnGap: 14,
        rowGap: 10,
      };

  const oppPanel = (
    <PlayerPanel
      view={opponent}
      name={opponentName}
      faction={opponentFaction}
      subtitle={opponentCity ? `of ${opponentCity}` : undefined}
      side="opponent"
      theme={theme}
      targetable={targeting?.candidatePlayers.has(opponent.player_id) && !animating}
      targetPicked={targeting?.pickedPlayers.has(opponent.player_id)}
      onTargetClick={
        targeting
          ? () => targeting.onToggle({ kind: "player", id: opponent.player_id })
          : undefined
      }
      onInvalidTargetClick={
        targeting && !targeting.candidatePlayers.has(opponent.player_id)
          ? () => playSfx("ai_notice_or_warning_sound")
          : undefined
      }
      onOpenGraveyard={() => setOpenZone("opp_graveyard")}
      onOpenExile={() => setOpenZone("opp_exile")}
      graveyardHighlighted={zoneCandidacy.opp_graveyard.size > 0}
      exileHighlighted={zoneCandidacy.opp_exile.size > 0}
      lifeLossThisTurn={lifeLossThisTurn[opponent.player_id]}
    />
  );
  const youPanel = (
    <PlayerPanel
      view={you}
      name={youName}
      faction={yourFaction}
      subtitle={youSubtitle ?? undefined}
      side="you"
      theme={theme}
      targetable={targeting?.candidatePlayers.has(you.player_id) && !animating}
      targetPicked={targeting?.pickedPlayers.has(you.player_id)}
      onTargetClick={
        targeting
          ? () => targeting.onToggle({ kind: "player", id: you.player_id })
          : undefined
      }
      onInvalidTargetClick={
        targeting && !targeting.candidatePlayers.has(you.player_id)
          ? () => playSfx("ai_notice_or_warning_sound")
          : undefined
      }
      onOpenDeck={() => setOpenZone("deck")}
      onOpenGraveyard={() => setOpenZone("graveyard")}
      onOpenExile={() => setOpenZone("exile")}
      deckHighlighted={zoneCandidacy.deck.size > 0}
      graveyardHighlighted={zoneCandidacy.graveyard.size > 0}
      exileHighlighted={zoneCandidacy.exile.size > 0}
      lifeLossThisTurn={lifeLossThisTurn[you.player_id]}
    />
  );

  // Rendered at BattlePage level so the clone outlives the hand slot
  // when the engine dispatch unmounts it. `centralRect` is used to
  // clip the clone so it can't paint outside the central play area
  // (sidebar, page margins).
  const playCloneEl = playClone ? (
    <PlayCloneCard
      playClone={playClone}
      theme={theme}
      centralRect={centralRect}
    />
  ) : null;
  // Anchor for cards animating to/from the opp's hidden hand. Lives
  // inside the opp battle area so plays slide in from there rather
  // than from the top of the viewport. `oppHandAnchorRef` (declared
  // at the top of the component because `playEvents` reads it from
  // an effect-style callback that fires before the early returns
  // below get to run) lets the animation driver read the anchor's
  // viewport rect so it can mount a PlayClone in the same spot for
  // opp-play hovers.
  const opponentHandAnchor = (
    <div
      ref={opponentHandAnchorRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: 180,
        height: 1,
        pointerEvents: "none",
        opacity: 0,
      }}
    >
      {animations.bounceAnchors.map((a) => (
        <BounceAnchorGhost
          key={a.eid}
          eid={a.eid}
          onComplete={animations.completeBounceAnchor}
        />
      ))}
    </div>
  );
  return (
    <BoardRefsProvider value={animations.boardRefs}>
    <CardOverridesProvider value={animations.overridesValue}>
    {playCloneEl}
    <CombatLungeOverlay
      items={animations.lungeItems}
      theme={theme}
      onComplete={animations.completeLunge}
    />
    <div
      ref={boardRef}
      style={{
        width: "100%",
        height: "100%",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        display: "grid",
        gridTemplateColumns: gridConfig.columns,
        gridTemplateRows: gridConfig.rows,
        gridTemplateAreas: gridConfig.areas,
        columnGap: gridConfig.columnGap,
        rowGap: gridConfig.rowGap,
        padding: isCompact ? "8px 8px" : "12px 16px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{
          gridArea: "header",
          margin: isCompact ? "-8px -8px 0" : "-12px -16px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: isCompact ? "6px 8px" : "6px 16px",
          borderBottom: `2px solid ${theme.panelEdge}`,
          background: theme.panel,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
          }}
        >
          {theme.ornament} BATTLE
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {spectating && (
            <SpectatorHeader
              snapshot={snapshot}
              perspective={spectatorPerspective}
              onPick={setSpectatorPerspective}
              theme={theme}
            />
          )}
          <VolumePanel />
          {spectating ? (
            <button onClick={() => void onConcede()} {...ghostBtn(theme)}>
              LEAVE
            </button>
          ) : canClaimVictory ? (
            <button onClick={() => void onClaimVictory()} {...primaryBtn(theme, false)}>
              CLAIM VICTORY
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <button onClick={() => void onConcede()} {...ghostBtn(theme)}>
                CONCEDE
              </button>
              {claimStatus && (
                <span
                  style={{
                    fontSize: 11,
                    color: theme.inkFaint,
                    fontFamily: theme.fontDisplay,
                    letterSpacing: theme.letterSpacing,
                    maxWidth: 380,
                    textAlign: "right",
                  }}
                >
                  {claimStatus.message}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {isCompact ? (
        <div
          style={{
            gridArea: "rail",
            minHeight: 0,
            position: "relative",
            // Don't clip — when expanded, the rail grows wider than
            // its grid cell and overlays the field area to the right.
            overflow: "visible",
          }}
        >
          <PlayerRail
            theme={theme}
            expanded={railExpanded}
            onCollapse={() => setRailExpanded(false)}
            expandedContent={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <div style={{ flex: 1, display: "flex", minHeight: 0 }}>{oppPanel}</div>
                <div style={{ flex: 1, display: "flex", minHeight: 0 }}>{youPanel}</div>
              </div>
            }
            opponent={{
              view: opponent,
              faction: opponentFaction,
              targetable: targeting?.candidatePlayers.has(opponent.player_id) && !animating,
              targetPicked: targeting?.pickedPlayers.has(opponent.player_id),
              onTargetClick: targeting
                ? () => targeting.onToggle({ kind: "player", id: opponent.player_id })
                : undefined,
              onInvalidTargetClick:
                targeting && !targeting.candidatePlayers.has(opponent.player_id)
                  ? () => playSfx("ai_notice_or_warning_sound")
                  : undefined,
              onExpand: () => setRailExpanded(true),
              onOpenGraveyard: () => setOpenZone("opp_graveyard"),
              onOpenExile: () => setOpenZone("opp_exile"),
              graveyardHighlighted: zoneCandidacy.opp_graveyard.size > 0,
              exileHighlighted: zoneCandidacy.opp_exile.size > 0,
              lifeLossThisTurn: lifeLossThisTurn[opponent.player_id],
            }}
            you={{
              view: you,
              faction: yourFaction,
              targetable: targeting?.candidatePlayers.has(you.player_id) && !animating,
              targetPicked: targeting?.pickedPlayers.has(you.player_id),
              onTargetClick: targeting
                ? () => targeting.onToggle({ kind: "player", id: you.player_id })
                : undefined,
              onInvalidTargetClick:
                targeting && !targeting.candidatePlayers.has(you.player_id)
                  ? () => playSfx("ai_notice_or_warning_sound")
                  : undefined,
              onExpand: () => setRailExpanded(true),
              onOpenDeck: () => setOpenZone("deck"),
              onOpenGraveyard: () => setOpenZone("graveyard"),
              onOpenExile: () => setOpenZone("exile"),
              deckHighlighted: zoneCandidacy.deck.size > 0,
              graveyardHighlighted: zoneCandidacy.graveyard.size > 0,
              exileHighlighted: zoneCandidacy.exile.size > 0,
              lifeLossThisTurn: lifeLossThisTurn[you.player_id],
            }}
          />
        </div>
      ) : (
        <>
          <div style={{ gridArea: "oppinfo", minHeight: 0, display: "flex" }}>{oppPanel}</div>
          <div style={{ gridArea: "youinfo", minHeight: 0, display: "flex" }}>{youPanel}</div>
        </>
      )}

      {/* Sizing sentinel for the central play area. Pinned to the grid
          column that holds oppfield/phase/youfield; never paints. Read
          by the play-clone clip so cards can't render outside the
          central play area. */}
      {!isCompact && (
        <div
          ref={centralAreaRef}
          aria-hidden
          style={{
            gridColumn: 2,
            gridRow: "2 / 5",
            pointerEvents: "none",
          }}
        />
      )}
      {isCompact && (
        <div
          ref={centralAreaRef}
          aria-hidden
          style={{
            gridColumn: 2,
            gridRow: "2 / 4",
            pointerEvents: "none",
          }}
        />
      )}
      <div
        style={{
          gridArea: "oppfield",
          minHeight: 0,
          // `overflow-x: clip` blocks horizontal bleed into the
          // sidebar; `overflow-y: visible` lets the combat lunge
          // bleed up and down past the field's outer edge into the
          // surrounding margin / phase-divider seam. CSS only
          // coerces `visible` away when the other axis is auto /
          // scroll / hidden — `clip` is exempt, which is the only
          // reason this pair is legal. The Field itself owns the
          // horizontal scrollbar when the row overflows.
          overflowX: "clip",
          overflowY: "visible",
          position: "relative",
        }}
        data-battle-drop-zone
      >
        <Field
          isYou={false}
          faction={OPPONENT.faction}
          units={opponent.battle}
          theme={theme}
          interaction={oppInteraction}
          cardRefs={cardRefs}
          muted={fieldMuted}
        />
        {opponentHandAnchor}
      </div>

      {!isCompact && (
        <div
          style={{
            gridArea: "phase",
            position: "relative",
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          data-battle-drop-zone
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${theme.panelEdgeStrong}, transparent)`,
            }}
          />
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
              textTransform: "uppercase",
              color: theme.inkDim,
              background: theme.bgDeep,
              padding: "3px 14px",
              borderRadius: theme.radius.chip,
              border: `1px solid ${theme.panelEdge}`,
              position: "relative",
              zIndex: 1,
            }}
          >
            {theme.ornament} Turn {snapshot.turn} — {phaseLabel(pending)}
            {pending && pending.player !== viewer ? " (opponent)" : ""} {theme.ornament}
          </div>
        </div>
      )}

      <div
        style={{
          gridArea: "youfield",
          minHeight: 0,
          // Same as oppfield: clip horizontally so the lunge can't
          // bleed into the sidebar, but allow vertical bleed for
          // the lunge to extend into the surrounding margin.
          overflowX: "clip",
          overflowY: "visible",
        }}
        data-battle-drop-zone
      >
        <Field
          isYou={true}
          faction={yourFaction}
          units={you.battle}
          theme={theme}
          interaction={ownInteraction}
          cardRefs={cardRefs}
          muted={fieldMuted}
        />
      </div>

      <div
        style={{
          gridArea: "sidebar",
          // Compact wraps both children in a single panel chrome;
          // comfortable lets each child render its own panelStyle.
          ...(isCompact ? panelStyle(theme) : {}),
          display: "flex",
          flexDirection: "column",
          gap: isCompact ? 0 : 10,
          minHeight: 0,
          // 4px right reserve in compact for the panel's drop shadow.
          marginRight: isCompact ? 4 : 0,
          overflow: "hidden",
        }}
      >
        {showLogInsteadOfPreview ? (
          logSlot
        ) : showPreviewOnly ? (
          previewSlot
        ) : (
          <>
            <div style={{ flex: "0 1 auto", minHeight: 0, maxHeight: "60%", display: "flex", flexDirection: "column" }}>{previewSlot}</div>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>{logSlot}</div>
          </>
        )}
      </div>

      <div style={{ gridArea: "hand", minHeight: 0, position: "relative" }}>
        <HandRow
          hand={hand}
          mana={you.mana}
          theme={theme}
          selectedIdx={selection.handIdx}
          canPlay={isMain}
          castingEid={playClone?.eid ?? null}
          hiddenEids={
            castingDissolvedEid != null ? [castingDissolvedEid] : undefined
          }
          noTargetEids={noTargetHandEids}
          pendingManaCost={
            // Only deduct the optimistic cost while the snapshot's
            // mana still matches what it was when we committed —
            // otherwise the engine's wire event has already landed
            // and `mana` reflects the deduction, so adding `cost` on
            // top would double-debit for the one paint between the
            // dispatch's commit and the useEffect that clears
            // `pendingPlay`, briefly snapping the row two pips lower
            // than it should be.
            pendingPlay != null && you.mana === pendingPlay.manaBefore
              ? pendingPlay.cost
              : 0
          }
          onSelect={(idx, card) => {
            if (card == null || idx == null) {
              setSelection((s) => ({ ...s, inspected: null, handIdx: null }));
            } else {
              onInspect(card, "hand", idx);
            }
          }}
          onPlayByDrag={
            isMain && !spectating
              ? (idx, releaseRect) => {
                  const card = hand[idx];
                  if (!card) return;
                  if (card.cost > you.mana) return;
                  submitAction(
                    { kind: "play", hand_idx: idx },
                    { releaseRect },
                  );
                }
              : undefined
          }
          onPlayByKeyboard={
            isMain && !spectating
              ? (idx, releaseRect) => {
                  const card = hand[idx];
                  if (!card) return;
                  if (card.cost > you.mana) return;
                  submitAction(
                    { kind: "play", hand_idx: idx },
                    { releaseRect },
                  );
                }
              : undefined
          }
          // Spells that target a card in your own hand (e.g. Scheduled
          // Blast's "exile a creature from your hand") turn this row into
          // the target picker. Gate on `!animating` so a pick can't land
          // against a stale snapshot mid-replay — matching the
          // battlefield picker's guard.
          targetPicking={targeting != null && !animating}
          targetCandidateEids={targeting?.candidateEntities}
          targetPickOrder={targeting?.pickedEntities}
          onPickTarget={(eid) =>
            targeting?.onToggle({ kind: "entity", id: eid })
          }
        />
      </div>

      {spectating ? (
        snapshot.result && gameOverMinimized ? (
          <EndTurnButton
            label="CONTINUE"
            onClick={() => setGameOverMinimized(false)}
            disabled={false}
            accent={theme.accent}
            theme={theme}
          />
        ) : null
      ) : snapshot.result && gameOverMinimized ? (
        <EndTurnButton
          label="CONTINUE"
          onClick={() => setGameOverMinimized(false)}
          disabled={false}
          accent={theme.accent}
          theme={theme}
        />
      ) : (
        <EndTurnButton
          label={animationSettled ? (endTurn?.label ?? "…") : "…"}
          onClick={() => {
            if (animationSettled && endTurn?.action) submitAction(endTurn.action);
          }}
          disabled={
            !animationSettled ||
            endTurn == null ||
            endTurn.disabled ||
            endTurn.action == null
          }
          accent={endTurnAccent}
          theme={theme}
        />
      )}

      {blocksPhaseForViewer && (
        <BlockLines
          container={boardRef.current}
          cardRefs={cardRefs}
          pairs={linePairs}
          pendingBlockerId={selection.pendingBlocker}
          theme={theme}
        />
      )}
      {blocksPhaseForViewer && (
        <BlockHint selection={selection} theme={theme} faction={yourFaction} />
      )}

      {targeting && !spectating && (
        <TargetOverlay
          request={targeting.request}
          pickCount={targeting.pickedEntities.size + targeting.pickedPlayers.size}
          onConfirm={targeting.onConfirm}
          onClear={targeting.onClear}
          theme={theme}
          animating={animating}
        />
      )}

      {!spectating &&
        snapshot.pending?.kind === "choice" &&
        snapshot.pending.player === viewer && (
          <ChoiceModal
            request={snapshot.pending.request}
            onSubmit={(picks) => submitAction({ kind: "choice", picks })}
            theme={theme}
            animating={animating}
          />
        )}

      {/* Mount the GameOverBanner only after the animation queue has
       *  drained, so the final layout beat finishes painting before
       *  the modal pops up over it. */}
      {snapshot.result && !gameOverMinimized && !animating && (
        <GameOverBanner
          result={snapshot.result}
          viewer={viewer}
          theme={theme}
          onNewGame={() => void onConcede()}
          onMinimize={() => setGameOverMinimized(true)}
          headlineOverride={
            spectating
              ? spectatorHeadline(snapshot.result, snapshot)
              : undefined
          }
          buttonLabel={
            spectating
              ? "RETURN TO ROOM"
              : challengeReturnTo
                ? "RETURN"
                : roomId
                  ? "RETURN TO ROOM"
                  : "NEW GAME"
          }
        />
      )}

      {openZone && (() => {
        const cards =
          openZone === "deck"
            ? (snapshot.players[viewer].deck ?? [])
            : openZone === "graveyard"
              ? (snapshot.players[viewer].graveyard ?? [])
              : openZone === "exile"
                ? (snapshot.players[viewer].exile ?? [])
                : openZone === "opp_graveyard"
                  ? (snapshot.players[1 - viewer].graveyard ?? [])
                  : openZone === "opp_exile"
                    ? (snapshot.players[1 - viewer].exile ?? [])
                    : revealedCards;
        // For the "reveal" zone modal, candidacy is the intersection
        // of the revealed eids and the request's pickable candidates.
        // The viewable-only entries (greyed out in the picker) appear
        // in revealedCards so the player sees them, but they're NOT
        // in `candIds` — so they render as inert. This is the
        // `viewable` opt of `choose_targets` rendered correctly.
        const candIds =
          openZone === "reveal"
            ? new Set(
                revealedCards
                  .map((c) => c.entity_id)
                  .filter((id) => targeting?.candidateEntities.has(id) ?? false),
              )
            : zoneCandidacy[openZone];
        // Modal is browse-only unless a target picker is open with candidates here.
        let zoneTargeting: ZoneViewerTargeting | undefined;
        if (targeting && candIds.size > 0) {
          zoneTargeting = {
            candidateEntities: candIds,
            pickedEntities: targeting.pickedEntities,
            min: targeting.request.min,
            max: targeting.request.max,
            prompt: targeting.request.prompt ?? null,
            onTogglePick: (eid) =>
              targeting.onToggle({ kind: "entity", id: eid }),
            onConfirm: targeting.onConfirm,
            onClear: targeting.onClear,
          };
        }
        return (
          <ZoneViewer
            zone={openZone}
            cards={cards}
            theme={theme}
            onClose={() => {
              if (openZone === "reveal") {
                setDismissedRevealKey(pendingKey(snapshot.pending));
                if (shouldAcknowledgeRevealClose(openZone, snapshot.pending, viewer)) {
                  submitAction({ kind: "target", picks: [] });
                }
              }
              setOpenZone(null);
            }}
            targeting={zoneTargeting}
          />
        );
      })()}
    </div>
    </CardOverridesProvider>
    </BoardRefsProvider>
  );
}

// Walk the snapshot for any visible card's faction. Used by
// `onInspectCardFromLog` when constructing the optimistic placeholder
// for a card not yet in the catalog cache — Card's `factionFor` is
// strict, so a placeholder needs a real faction to render. Opponent
// first (most log clicks are about their plays), then own; if neither
// player has a single visible card with a faction (extreme edge case:
// empty-board first turn after mulligan, opponent's hand hidden, deck
// hidden), fall back to the first faction in the master roster — any
// real key beats a crash.
export function pickAnyVisibleFaction(
  snap: GameView | null,
  viewer: PlayerId,
): string {
  if (snap) {
    const order: PlayerId[] = [(1 - viewer) as PlayerId, viewer];
    for (const pid of order) {
      const seat = snap.players[pid];
      if (!seat) continue;
      const zones: (CardView[] | undefined | null)[] = [
        seat.battle,
        seat.hand ?? undefined,
        seat.graveyard ?? undefined,
        seat.exile ?? undefined,
        seat.deck ?? undefined,
      ];
      for (const zone of zones) {
        if (!zone) continue;
        for (const c of zone) {
          if (c.faction) return c.faction;
        }
      }
    }
  }
  return "humans";
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Hit-tests rectangles directly so drop targeting works through any
// CSS transforms on intermediate wrappers.
function findIncomingAttackerAt(
  x: number,
  y: number,
  cardRefs: Map<number, HTMLDivElement | null>,
  incomingAttackers: Set<number>,
): number | null {
  for (const id of incomingAttackers) {
    const el = cardRefs.get(id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
  }
  return null;
}

function flattenPairs(blocks: Map<number, Set<number>>): { attackerId: number; blockerId: number }[] {
  const out: { attackerId: number; blockerId: number }[] = [];
  for (const [attackerId, bs] of blocks) {
    for (const blockerId of bs) out.push({ attackerId, blockerId });
  }
  return out;
}

// True iff any step in the batch carries a Played/Cast battle-feed
// entry for the given eid. Used to detect a no-target cancel: when
// the player drags a card from hand and the engine quietly returns it
// because there are no legal targets, the response has no such entry,
// and we surface that as the error chirp.
// Returns true if `next` differs from `prev` in any way the user can
// see — cards moved between zones, took damage, lost life, etc. Used
// by `playSteps` to decide whether to run the post-dispatch motion
// beat for this step or to fast-forward through it. Pure phase ticks
// (e.g. main → attackers) have no visible diff and the user expects
// them to land instantly.
/** True if the wire events committed a `play`/`cast` for `eid` —
 *  the card actually left hand and entered battle (or the stack /
 *  graveyard via spell resolution). The engine's cast pipeline hides
 *  the hand→stack hop and only dispatches a `CardEnteredZone` for
 *  the stack→destination resolve half, so the move event's `from`
 *  field is `"stack"`. `"hand"` stays in the predicate as a safety
 *  net for any future engine path that surfaces the silent first
 *  half. Used by `submitAction` to detect the "engine refused the
 *  cast" no-target path: when the event stream carries no
 *  Move-from-(stack|hand) for the eid, the card is still in hand and
 *  we play the warning chirp + clean up the hover clone. */
function eventsCommittedCast(events: WireEvent[], eid: number): boolean {
  for (const ev of events) {
    if (ev.k !== "move") continue;
    if (ev.eid !== eid) continue;
    if (ev.from === "stack" || ev.from === "hand") return true;
  }
  return false;
}

function unavailableOwnSet(
  battle: CardView[],
  pending: GameView["pending"],
  viewer: PlayerId,
): Set<number> {
  const out = new Set<number>();
  if (!pending) return out;
  // Only dim "you can't pick this" on YOUR turn / your decision.
  // During the opponent's attackers / blocks pending, none of your
  // creatures are subject to the dim; otherwise the snapshot's
  // `can_attack` (an own-turn property) shifts every step the engine
  // moves through and your board flickers grey ↔ full-colour.
  if (pending.player !== viewer) return out;
  if (pending.kind === "attackers") {
    for (const c of battle) {
      if (c.type === "creature" && !c.can_attack) out.add(c.entity_id);
    }
  } else if (pending.kind === "blocks") {
    for (const c of battle) {
      if (c.type !== "creature") continue;
      if (!canBlockAny(c, pending.attackers, pending.illegal_blockers)) {
        out.add(c.entity_id);
      }
    }
  }
  return out;
}

